import React, { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { saveUserData, savePortalStrategy, getSavedStrategies, deletePortalStrategy } from "./dataStorage";
import { useAuth } from "./AuthContext";
import { generateLoanReport, downloadReport, generateBeautifulPDF, downloadBeautifulReport, generateFancyReport, downloadFancyReport, generateNewBeautifulPDF, downloadNewBeautifulReport, svgToDataURL } from "./aiReportService";
import html2canvas from 'html2canvas';
import AIAgent from "./AIAgent";
import { captureAndSendScreenshot } from './emailService';
import { logUserActivity } from './analytics';
import PortalChart from './PortalChart';
import BoxSlider, { BOX_SLIDER_GLOBAL_STYLE } from './BoxSlider';
import { usePortalCalculations, sampleAtMonth } from './usePortalCalculations';
import { getAnnualRateForPlan } from "./loanConstants";
import { computePortalSchedule } from "./portalSimulation";
import { buildPortalYearlyChartData, buildPortalYearlyTableData } from "./portalChartData";
import landingConfig from './legend.landing.json';
import {
  computeComplementPanels,
  TutorialComplementBlurStack,
} from "./MobilePortalTutorialOverlay.js";

/** Zelfde blur als `shouldBlurRight` (eerste tutorialdeel): blur(8px) + rgba(255,255,255,0.4). */
const PC_GUIDE_SPOTLIGHT_BACKDROP_BLUR = "blur(8px)";
const PC_GUIDE_SPOTLIGHT_OVERLAY_BG = "rgba(255, 255, 255, 0.4)";

const PC_GUIDE_SPOTLIGHT_Z_BACKDROP = 150;
const PC_GUIDE_SPOTLIGHT_Z_BLOCK = 151;
const PC_GUIDE_SPOTLIGHT_Z_CARD = 152;

/** Set to true to show the "We horen graag jouw mening" feedback module. */
const SHOW_FEEDBACK_MODULE = false;

/** When true, "Opgeslagen strategieën" and strategy summary use the same top bar and body area (no separate overlay with own menu). When false, legacy full-screen overlays with their own header are used. */
const USE_UNIFIED_TOP_BAR = true;

/** PC-invoerrondleiding: zelfde volgorde als mobiele portal (MENU_OPTIONS / categorieën). */
const PC_GUIDE_INPUT_FLOW = [
  "inputIntro",
  "inputGegevens",
  "inputAflosstrategie",
  "inputDelay",
  "inputInvesteren",
  "inputEenmalig",
  "chartTiles",
  "chartVerloop",
  "chartBetaalschema",
];

function domRectToHole(el) {
  const b = el.getBoundingClientRect();
  return { left: b.left, top: b.top, width: b.width, height: b.height };
}

/** Tutorial-uitlegkaart: onder samenvatting; verloop links naast de grafiek; betaalschema erboven. */
function computeGuideTutorialCardLayout(guideStep, anchorEl, vw, vh) {
  if (!anchorEl) return null;
  const anchor = anchorEl.getBoundingClientRect();
  if (anchor.width <= 0 || anchor.height <= 0) return null;
  const GAP = 14;
  const M = 24;
  const minW = 600;
  if (guideStep === "chartTiles") {
    const width = Math.min(Math.max(anchor.width, minW), vw - 2 * M);
    let left = anchor.left + (anchor.width - width) / 2;
    left = Math.max(M, Math.min(left, vw - width - M));
    const top = anchor.bottom + GAP;
    const spaceBelow = vh - top - M;
    return {
      placement: "below",
      top,
      left,
      width,
      maxHeight: Math.min(240, Math.max(120, spaceBelow - 80)),
    };
  }
  if (guideStep === "chartVerloop") {
    const leftColMaxW = 440;
    const leftColMinW = 260;
    const availLeft = anchor.left - GAP - M;
    if (availLeft >= leftColMinW) {
      const width = Math.min(leftColMaxW, availLeft);
      const left = Math.max(M, anchor.left - GAP - width);
      const top = Math.max(M, anchor.top);
      const maxHeight = Math.min(580, vh - top - M);
      return {
        placement: "leftSide",
        top,
        left,
        width,
        maxHeight,
      };
    }
    const width = Math.min(Math.max(anchor.width, minW), vw - 2 * M);
    let left = anchor.left + (anchor.width - width) / 2;
    left = Math.max(M, Math.min(left, vw - width - M));
    const bottom = vh - anchor.top + GAP;
    const spaceAbove = anchor.top - GAP - M;
    return {
      placement: "above",
      bottom,
      left,
      width,
      maxHeight: Math.min(280, Math.max(120, spaceAbove - 16)),
    };
  }
  if (guideStep === "chartBetaalschema") {
    const width = Math.min(Math.max(anchor.width, minW), vw - 2 * M);
    let left = anchor.left + (anchor.width - width) / 2;
    left = Math.max(M, Math.min(left, vw - width - M));
    const bottom = vh - anchor.top + GAP;
    const spaceAbove = anchor.top - GAP - M;
    return {
      placement: "above",
      bottom,
      left,
      width,
      maxHeight: Math.min(240, Math.max(120, spaceAbove - 16)),
    };
  }
  return null;
}

const PC_GUIDE_CHART_CARD = {
  chartTiles: {
    title: "Samenvatting",
    body:
      "De blauwe tegels vatten je scenario samen: totale kosten, betaalde rente, gekozen stelsel, je strategie (alleen aflossen, combineren of nadruk op investeren) en de verwachte looptijd. Zie je “Niet binnen termijn” bij looptijd? Dan is je maandbedrag te laag om binnen de gekozen termijn af te lossen.",
  },
  chartVerloop: {
    title: "Verloop studieschuld",
    body:
      "De grafiek laat zien hoe je openstaande schuld (blauw) en cumulatieve rente (grijs) in de tijd verlopen. Zet je investeren aan, dan zie je ook je belegd vermogen (groen). Beweeg met de muis om waarden per jaar te lezen. Kruist groen de blauwe lijn vóór het einde, dan kun je in één keer aflossen via je investering.",
  },
  chartBetaalschema: {
    title: "Betaalschema",
    body:
      "Hier zie je het verloop van je maandelijkse aflossing (en eventueel investering) door de tijd, inclusief jaarlijkse stijging als je die hebt ingeschakeld. De lijn stopt zodra de lening is afgelost.",
  },
};

const PC_GUIDE_STEP_TO_SIDEBAR = {
  inputIntro: "info",
  inputGegevens: "gegevens",
  inputAflosstrategie: "aflosstrategie",
  inputDelay: "delay",
  inputInvesteren: "investeren",
  inputEenmalig: "eenmalig",
};

const PC_GUIDE_SIDEBAR_TO_STEP = {
  info: "inputIntro",
  gegevens: "inputGegevens",
  aflosstrategie: "inputAflosstrategie",
  delay: "inputDelay",
  investeren: "inputInvesteren",
  eenmalig: "inputEenmalig",
};

const PC_GUIDE_INPUT_CARD_TITLE = {
  inputIntro: "Jouw invoervelden",
  inputGegevens: "Basisinformatie input",
  inputAflosstrategie: "Maandbedrag aflossen",
  inputDelay: "Aflossingen uitstellen",
  inputInvesteren: "Investeren naast aflossen",
  inputEenmalig: "Eenmalige aflossingen",
};

/** Zelfde toelichting per onderwerp als mobiele CATEGORY_TUTORIAL_EXPLANATIONS. */
const PC_GUIDE_INPUT_CARD_BODY = {
  gegevens:
    "Dit zijn de uitgangspunten van je lening: hoe hoog is je studieschuld, kies je het 15- of 35-jarig aflossingsschema, en ben je al begonnen met aflossen? Die gegevens bepalen samen met de rest wat het wettelijke minimum is en hoe snel je schuld daalt.",
  aflosstrategie:
    "Hier kies je hoeveel je vrijwillig per maand aflost boven het minimum, of je dat bedrag jaarlijks laat meestijgen, en eventueel een maximum zodat je maandlast binnen een grens blijft. Alles wat je hier wijzigt, zie je direct terug in de tegels en de grafiek.",
  delay:
    "Hier plan je een periode waarin je (gedeeltelijk) geen aflossing betaalt. De portal rekent rente over die periode mee, zodat je ziet wat uitstel doet met je looptijd en totale kosten — handig bij tijdelijk minder inkomen.",
  investeren:
    "Als je wilt, kun je naast aflossen ook maandelijks beleggen met een door jou gekozen groeipercentage. De portal laat zien hoe dat samen met aflossen uitpakt voor je schuld en looptijd (binnen de gekozen grenzen).",
  eenmalig:
    "Hier voeg je eenmalige extra aflossingen toe en kies je in welk jaar van je plan ze vallen — bijvoorbeeld een bonus of een meevaller. Zo zet je incidentele bedragen in je scenario.",
};

const PC_GUIDE_INPUT_INTRO_BODY =
  "Links stel je stap voor stap je lening en keuzes in — dezelfde vaste volgorde als in de mobiele portal. Je begint bij de basis en werkt door naar maandbedrag, uitstellen, beleggen en eenmalige aflossingen. Alles wat je aanpast, zie je direct rechts terug in je samenvatting en grafiek. Klik op een pictogram om naar een onderdeel te springen, of kies Volgende om met het eerste invoerblok te beginnen.";

/** Blue gradient – used for buttons, tiles, backgrounds on PC */
const PORTAL_GRADIENT = {
  background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)",
  backgroundSize: "cover",
  backgroundPosition: "center",
};
const PORTAL_ACCENT = "#3399FF";
const PORTAL_ACCENT_RGBA = (a) => `rgba(51,153,255,${a})`;

function Info({ text, variant = "light" }) {
  const [open, setOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const iconRef = useRef(null);
  const iconStyle = { background: "#ffffff", color: "#374151", border: "1px solid #e5e7eb" };

  const updateTooltipPosition = useCallback(() => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    const tw = 280;
    const th = 180;
    const pad = 10;
    let left = rect.left + rect.width / 2 - tw / 2;
    let top = rect.top - th - 8;
    if (left < pad) left = pad;
    if (left + tw > window.innerWidth - pad) left = window.innerWidth - tw - pad;
    if (top < pad) {
      top = rect.bottom + 8;
      if (top + th > window.innerHeight - pad) top = window.innerHeight - th - pad;
    } else if (top + th > window.innerHeight - pad) {
      top = window.innerHeight - th - pad;
    }
    setTooltipStyle({
      position: "fixed",
      left: `${left}px`,
      top: `${top}px`,
      zIndex: 99999,
      whiteSpace: "normal",
      width: tw,
      maxWidth: Math.min(320, window.innerWidth - 20),
      maxHeight: window.innerHeight - 20,
      overflowY: "auto",
      background: "#111827",
      color: "#F9FAFB",
      border: "1px solid #1F2937",
      borderRadius: 10,
      padding: "12px 16px",
      boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
      pointerEvents: "none"
    });
  }, []);

  useEffect(() => {
    if (open) updateTooltipPosition();
  }, [open, updateTooltipPosition]);

  return (
    <span
      style={{ position: "relative", display: "inline-block", marginLeft: 6 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        ref={iconRef}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 18,
          height: 18,
          borderRadius: 9999,
          fontSize: 12,
          cursor: "help",
          ...iconStyle
        }}
      >i</span>
      {open && tooltipStyle.left != null && <span style={tooltipStyle}>{text}</span>}
    </span>
  );
}

function InfoSummary({ text }) {
  const [open, setOpen] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const iconRef = useRef(null);

  const handleMouseEnter = (e) => {
    setOpen(true);
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipWidth = 280; // approximate width
      const tooltipHeight = 100; // approximate height
      
      // Position above the icon, centered
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      let top = rect.top - tooltipHeight - 8;
      
      // Ensure it doesn't go off screen
      if (left < 10) left = 10;
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }
      if (top < 10) {
        // If not enough space above, show below
        top = rect.bottom + 8;
      }
      
      setTooltipStyle({
        position: "fixed",
        left: `${left}px`,
        top: `${top}px`,
        zIndex: 99999
      });
    }
  };

  return (
    <span 
      ref={iconRef}
      style={{ position: "relative", display: "inline-block", marginLeft: 4 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setOpen(false)}
    >
      <span style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: 9999,
        background: "#ffffff",
        color: "#374151",
        fontSize: 11,
        fontWeight: 600,
        border: "1px solid #e5e7eb",
        cursor: "help"
      }}>i</span>
      {open && typeof document !== "undefined" && document.body && ReactDOM.createPortal(
        <span style={{
          ...tooltipStyle,
          whiteSpace: "normal",
          minWidth: 220,
          maxWidth: 320,
          backgroundColor: "#111827",
          color: "#F9FAFB",
          border: "1px solid #374151",
          borderRadius: 10,
          padding: "12px 16px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
          pointerEvents: "none",
          fontSize: 13,
          lineHeight: "1.5",
        }}>{text}</span>,
        document.body
      )}
    </span>
  );
}

const inputStyle = {
  width: "100%",
  maxWidth: 320,
  padding: "12px 16px",
  borderRadius: 12,
  border: "2px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.05)",
  color: "#fff",
  outline: "none",
  fontSize: 16,
  fontWeight: 500,
  transition: "all 0.3s ease",
  backdropFilter: "blur(10px)",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  boxSizing: "border-box"
};

const inputStyleFocused = {
  ...inputStyle,
  border: "2px solid rgba(255,255,255,0.3)",
  background: "rgba(255,255,255,0.15)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
};

const inputStyleWhite = {
  width: "100%",
  maxWidth: 320,
  padding: "12px 16px",
  borderRadius: 12,
  border: "2px solid #e5e7eb",
  background: "#fff",
  color: "#111827",
  outline: "none",
  fontSize: 16,
  fontWeight: 500,
  transition: "all 0.3s ease",
  boxSizing: "border-box"
};

const inputStyleFocusedWhite = {
  ...inputStyleWhite,
  border: `2px solid ${PORTAL_ACCENT}`,
  boxShadow: `0 0 0 3px ${PORTAL_ACCENT_RGBA(0.2)}`
};

const SUMMARY_TILE_STYLE = {
  ...PORTAL_GRADIENT,
  borderRadius: 16,
  padding: "12px 16px",
  color: "#fff",
  boxShadow: `0 4px 12px ${PORTAL_ACCENT_RGBA(0.25)}`,
  minHeight: 72,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

export default function PortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const bg = "#e5e3e3";
  const cardShadow = "0 50px 140px rgba(0,0,0,0.25)";

  const [totalDebt, setTotalDebt] = useState(23000);
  const [repaymentPlan, setRepaymentPlan] = useState("15");
  const [hasStartedRepaying, setHasStartedRepaying] = useState(false);
  const [yearsAlreadyRepaid, setYearsAlreadyRepaid] = useState(0);
  const [minPayment, setMinPayment] = useState(0);
  const [showSalary, setShowSalary] = useState(false);
  const [yearlySalary, setYearlySalary] = useState(36000);

  const [monthlyPayment, setMonthlyPayment] = useState(250);
  const [enableGrowth, setEnableGrowth] = useState(false);
  const [growthRate, setGrowthRate] = useState(0); // 0-50 representing 0.0% to 5.0% in 0.1% steps
  const [enableSeparateGrowth, setEnableSeparateGrowth] = useState(false); // Separate growth for payments and investments
  const [investmentGrowthRate, setInvestmentGrowthRate] = useState(0); // 0-50 representing 0.0% to 5.0% in 0.1% steps
  const [enableDelay, setEnableDelay] = useState(false);
  const [delayYears, setDelayYears] = useState(0);
  const [delayMonths, setDelayMonths] = useState(0);
  const [enableInvestment, setEnableInvestment] = useState(false);
  const [monthlyInvestment, setMonthlyInvestment] = useState(0); // Maandelijkse investering in euro's
  const [startingInvestmentCapital, setStartingInvestmentCapital] = useState(""); // Startkapitaal investeringen
  const showInvestmentPayoffBadge = (Number(monthlyInvestment) || 0) > 0;

  // Derive enable state from values (no checkbox needed for delay / investment)
  useEffect(() => {
    setEnableDelay(delayYears > 0 || delayMonths > 0);
  }, [delayYears, delayMonths]);
  useEffect(() => {
    setEnableInvestment((Number(monthlyInvestment) || 0) > 0 || (Number(startingInvestmentCapital) || 0) > 0);
  }, [monthlyInvestment, startingInvestmentCapital]);
  useEffect(() => {
    setEnableGrowth(growthRate > 0);
  }, [growthRate]);
  const [oneTimeRepayments, setOneTimeRepayments] = useState([{ amount: 0, year: 1 }]);
  const [enableCustomRates, setEnableCustomRates] = useState(false);
  const [customRates, setCustomRates] = useState([]); // [{ year: 0, ratePct: 2.21 }, { year: 5, ratePct: 3 }, ...]
  const [dataSaved, setDataSaved] = useState(false);
  const [isSavingStrategy, setIsSavingStrategy] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  // PC: saved strategies flow (same as mobile)
  const [mainView, setMainView] = useState("portal"); // 'portal' | 'saved' | 'strategySummary'
  const [savedStrategies, setSavedStrategies] = useState([]);
  // PC: Draftr-style collapsible sidebar (icons only vs full form panel)
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [sidebarSection, setSidebarSection] = useState('info'); // 'info' | 'gegevens' | 'aflosstrategie' | 'delay' | 'investeren' | 'eenmalig' | 'feedback'
  const [sidebarHoverLabel, setSidebarHoverLabel] = useState(null); // section id when hovering an icon, for pill tooltip
  const [sidebarHoverPos, setSidebarHoverPos] = useState({ x: 0, y: 0 }); // cursor position for pill (follow cursor)
  const [sidebarFeedbackText, setSidebarFeedbackText] = useState('');
  const [sidebarFeedbackStudierichting, setSidebarFeedbackStudierichting] = useState('');
  const [sidebarFeedbackAnders, setSidebarFeedbackAnders] = useState('');
  const [sidebarFeedbackStatus, setSidebarFeedbackStatus] = useState('idle'); // idle | sending | success | error
  const SIDEBAR_STUDIERICHTINGEN = ['Economie', 'Finance', 'Sociologie', 'Psychologie', 'Rechten', 'Geneeskunde', 'Techniek/Engineering', 'Informatica/IT', 'Bedrijfskunde', 'Marketing', 'Communicatie', 'Onderwijs', 'Kunst & Cultuur', 'Geschiedenis', 'Filosofie', 'Biologie', 'Scheikunde', 'Wiskunde', 'Anders'];
  const SIDEBAR_WIDTH_COLLAPSED = 64;
  const SIDEBAR_WIDTH_EXPANDED = 400;
  const TOP_BAR_HEIGHT = 56;
  const SIDEBAR_SECTIONS = [
    { id: 'info', label: 'Info' },
    { id: 'gegevens', label: 'Basisinformatie input' },
    { id: 'aflosstrategie', label: 'Maandbedrag aflossen' },
    { id: 'delay', label: 'Aflossingen uitstellen' },
    { id: 'investeren', label: 'Investeren naast aflossen' },
    { id: 'eenmalig', label: 'Eenmalige aflossingen' },
    { id: 'feedback', label: 'Geef feedback' }
  ];
  const [loadedStrategy, setLoadedStrategy] = useState(null);
  const [pendingNavigateToSummary, setPendingNavigateToSummary] = useState(false);
  const [faqOpenIndices, setFaqOpenIndices] = useState([]); // multiple FAQ items can stay open
  const [showSaveNameModal, setShowSaveNameModal] = useState(false);
  const [saveNameValue, setSaveNameValue] = useState("");
  const [saveMessage, setSaveMessage] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [sharedHoverYear, setSharedHoverYear] = useState(null);
  const [chartWidth, setChartWidth] = useState(null);
  const chartRef = useRef(null);
  const paymentChartRef = useRef(null);
  const rightAreaRef = useRef(null);
  /** Scrollcontainer rechts (samenvatting/grafiek/betaalschema). */
  const rightPanelScrollRef = useRef(null);
  const guideSummarySectionRef = useRef(null);
  const guideSummaryHeadingRef = useRef(null);
  const guideSummaryTileRefs = useRef([]);
  const guideVerloopBlockRef = useRef(null);
  const guideBetaalBlockRef = useRef(null);
  const [guideRightHoleRects, setGuideRightHoleRects] = useState(null);
  const [guideTutorialCardLayout, setGuideTutorialCardLayout] = useState(null);
  const [guideViewportSize, setGuideViewportSize] = useState(() =>
    typeof window !== "undefined" ? { w: window.innerWidth, h: window.innerHeight } : { w: 1200, h: 800 }
  );
  const hoverTimeoutRef = useRef(null);
  
  // Guide state management (tutorial kan hier tijdelijk uitgezet worden; code blijft staan)
  const SHOW_TUTORIAL = true;
  /** none | welcome | inputIntro | inputGegevens | … | chart | completed */
  const [guideStep, setGuideStep] = useState('none');
  const [showInvestmentExplanation, setShowInvestmentExplanation] = useState(false);

  const isGuideSidebarInputPhase =
    guideStep === 'inputIntro' ||
    guideStep === 'inputGegevens' ||
    guideStep === 'inputAflosstrategie' ||
    guideStep === 'inputDelay' ||
    guideStep === 'inputInvesteren' ||
    guideStep === 'inputEenmalig';

  const isGuideRightSpotlightPhase =
    guideStep === "chartTiles" ||
    guideStep === "chartVerloop" ||
    guideStep === "chartBetaalschema";

  // Check if user has completed the guide
  useEffect(() => {
    if (!SHOW_TUTORIAL) {
      setGuideStep('completed');
      return;
    }
    const guideCompleted = localStorage.getItem('sparro-guide-completed');
    if (!guideCompleted && user) {
      setGuideStep('welcome');
    } else {
      setGuideStep('completed');
    }
  }, [user, SHOW_TUTORIAL]);

  useEffect(() => {
    if (isGuideSidebarInputPhase || isGuideRightSpotlightPhase) {
      setSidebarExpanded(true);
    }
  }, [isGuideSidebarInputPhase, isGuideRightSpotlightPhase]);

  const handleStartGuide = () => {
    setSidebarExpanded(true);
    setSidebarSection(PC_GUIDE_STEP_TO_SIDEBAR.inputIntro);
    setGuideStep('inputIntro');
  };

  const handleSkipGuide = () => {
    setGuideStep('completed');
    localStorage.setItem('sparro-guide-completed', 'true');
  };

  const handleCompleteGuide = useCallback(() => {
    setGuideStep("completed");
    localStorage.setItem("sparro-guide-completed", "true");
  }, []);

  const goGuideInputNext = useCallback(() => {
    if (guideStep === "chartBetaalschema") {
      handleCompleteGuide();
      return;
    }
    const i = PC_GUIDE_INPUT_FLOW.indexOf(guideStep);
    if (i < 0 || i >= PC_GUIDE_INPUT_FLOW.length - 1) return;
    const next = PC_GUIDE_INPUT_FLOW[i + 1];
    setGuideStep(next);
    const sb = PC_GUIDE_STEP_TO_SIDEBAR[next];
    if (sb) setSidebarSection(sb);
  }, [guideStep, handleCompleteGuide]);

  const goGuideInputPrevious = useCallback(() => {
    if (guideStep === "inputIntro") {
      setGuideStep("welcome");
      return;
    }
    const i = PC_GUIDE_INPUT_FLOW.indexOf(guideStep);
    if (i <= 0) return;
    const prev = PC_GUIDE_INPUT_FLOW[i - 1];
    setGuideStep(prev);
    const sb = PC_GUIDE_STEP_TO_SIDEBAR[prev];
    if (sb) setSidebarSection(sb);
  }, [guideStep]);

  const onSidebarIconClickForTutorial = useCallback(
    (secId) => {
      setSidebarSection(secId);
      setSidebarExpanded(true);
      if (!SHOW_TUTORIAL) return;
      if (guideStep === 'completed' || guideStep === 'welcome' || guideStep === 'none') return;
      const step = PC_GUIDE_SIDEBAR_TO_STEP[secId];
      if (step) setGuideStep(step);
    },
    [SHOW_TUTORIAL, guideStep]
  );

  const handleRestartTutorial = useCallback(() => {
    try {
      localStorage.removeItem('sparro-guide-completed');
    } catch {
      /* ignore */
    }
    setShowInvestmentExplanation(false);
    setGuideStep('welcome');
    setMainView('portal');
  }, []);

  const guideSidebarInputCard = useMemo(() => {
    const steps = new Set([
      'inputIntro',
      'inputGegevens',
      'inputAflosstrategie',
      'inputDelay',
      'inputInvesteren',
      'inputEenmalig',
    ]);
    if (!steps.has(guideStep)) return null;
    const title = PC_GUIDE_INPUT_CARD_TITLE[guideStep];
    const bodyKey =
      guideStep === 'inputIntro'
        ? null
        : {
            inputGegevens: 'gegevens',
            inputAflosstrategie: 'aflosstrategie',
            inputDelay: 'delay',
            inputInvesteren: 'investeren',
            inputEenmalig: 'eenmalig',
          }[guideStep];
    const body = guideStep === 'inputIntro' ? PC_GUIDE_INPUT_INTRO_BODY : PC_GUIDE_INPUT_CARD_BODY[bodyKey];
    return { title, body };
  }, [guideStep]);

  const guideChartCard = useMemo(() => {
    if (!isGuideRightSpotlightPhase) return null;
    return PC_GUIDE_CHART_CARD[guideStep] ?? null;
  }, [guideStep, isGuideRightSpotlightPhase]);

  /** Viewport minus highlight-gaten: zelfde complement-methode als mobiel, met PC-blurwaarden. */
  const guideSpotlightComplementPanels = useMemo(
    () =>
      isGuideRightSpotlightPhase && guideRightHoleRects?.length
        ? computeComplementPanels(
            { w: guideViewportSize.w, h: guideViewportSize.h },
            guideRightHoleRects
          )
        : [],
    [
      isGuideRightSpotlightPhase,
      guideRightHoleRects,
      guideViewportSize.w,
      guideViewportSize.h,
    ]
  );

  useLayoutEffect(() => {
    if (!isGuideRightSpotlightPhase || mainView !== "portal") {
      setGuideRightHoleRects(null);
      setGuideTutorialCardLayout(null);
      return;
    }
    const measure = () => {
      if (typeof window === "undefined") return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setGuideViewportSize({ w: vw, h: vh });
      if (guideStep === "chartTiles") {
        const holes = [];
        if (guideSummaryHeadingRef.current) holes.push(domRectToHole(guideSummaryHeadingRef.current));
        for (let i = 0; i < 5; i += 1) {
          const tileEl = guideSummaryTileRefs.current[i];
          if (tileEl) holes.push(domRectToHole(tileEl));
        }
        setGuideRightHoleRects(holes.length ? holes : null);
        setGuideTutorialCardLayout(
          computeGuideTutorialCardLayout(guideStep, guideSummarySectionRef.current, vw, vh)
        );
        return;
      }
      const el =
        guideStep === "chartVerloop"
          ? guideVerloopBlockRef.current
          : guideStep === "chartBetaalschema"
            ? guideBetaalBlockRef.current
            : null;
      if (!el) {
        setGuideRightHoleRects(null);
        setGuideTutorialCardLayout(null);
        return;
      }
      setGuideRightHoleRects([domRectToHole(el)]);
      setGuideTutorialCardLayout(computeGuideTutorialCardLayout(guideStep, el, vw, vh));
    };
    measure();
    const remeasureSoon = window.setTimeout(measure, 120);
    const scrollEl =
      guideStep === "chartTiles"
        ? guideSummarySectionRef.current
        : guideStep === "chartVerloop"
          ? guideVerloopBlockRef.current
          : guideStep === "chartBetaalschema"
            ? guideBetaalBlockRef.current
            : null;
    const raf = requestAnimationFrame(() => {
      scrollEl?.scrollIntoView({ block: "nearest", behavior: "auto" });
      measure();
    });
    const ro = new ResizeObserver(measure);
    if (guideStep === "chartTiles" && guideSummarySectionRef.current) {
      ro.observe(guideSummarySectionRef.current);
    } else if (guideStep === "chartVerloop" && guideVerloopBlockRef.current) {
      ro.observe(guideVerloopBlockRef.current);
    } else if (guideStep === "chartBetaalschema" && guideBetaalBlockRef.current) {
      ro.observe(guideBetaalBlockRef.current);
    }
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const sp = rightPanelScrollRef.current;
    if (sp) sp.addEventListener("scroll", measure, { passive: true });
    return () => {
      window.clearTimeout(remeasureSoon);
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      if (sp) sp.removeEventListener("scroll", measure);
    };
  }, [guideStep, isGuideRightSpotlightPhase, mainView, sidebarExpanded]);

  useEffect(() => {
    const onResize = () =>
      setGuideViewportSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /** Na tutorial: reset scroll/overflow (smooth scrollIntoView, blur overlay, sidebar lock). */
  useEffect(() => {
    if (guideStep !== "completed") return undefined;
    const t = window.setTimeout(() => {
      const sp = rightPanelScrollRef.current;
      if (sp) {
        sp.style.removeProperty("overflow-y");
        const max = Math.max(0, sp.scrollHeight - sp.clientHeight);
        if (sp.scrollTop > max) sp.scrollTop = max;
      }
      document.body.style.removeProperty("overflow");
      document.documentElement.style.removeProperty("overflow");
      window.scrollTo(0, 0);
    }, 0);
    return () => window.clearTimeout(t);
  }, [guideStep]);

  const uid = user?.uid ?? null;
  const summaryCalc = usePortalCalculations(loadedStrategy);
  const loadStrategies = useCallback(() => {
    getSavedStrategies(uid).then((list) => {
      setSavedStrategies((prev) => (list.length > 0 ? list : prev));
    });
  }, [uid]);
  useEffect(() => {
    if (uid) loadStrategies();
  }, [uid, loadStrategies]);

  // When user clicks "Samenvatting & aflosschema" on a saved strategy: build strategyData and navigate to /summary (same as after saving)
  useEffect(() => {
    if (!pendingNavigateToSummary || !loadedStrategy) return;
    const c = summaryCalc;
    if (c.totalDebt !== (loadedStrategy.totalDebt ?? 0) || (c.schedule?.length ?? 0) === 0) return;
    const totalTermYears = c.effectivePlanYears + (c.enableDelay ? c.delayYears : 0);
    const strategyData = {
      userName: user?.displayName || 'Unknown User',
      totalDebt: Number(c.totalDebt) || 0,
      repaymentSystem: Number(c.planYears) || 15,
      minPayment: Number(c.effectiveMinPayment) || 0,
      monthlyPayment: Number(c.constrainedMonthlyPayment) || 0,
      enableInvestment: Boolean(c.enableInvestment),
      monthlyInvestment: Number(c.monthlyInvestment) || 0,
      startingInvestmentCapital: Number(c.startingInvestmentCapital) || 0,
      payoffMonths: Number(c.payoffMonths) || 0,
      totalCost: Number(c.totalCostWithInvestments) || 0,
      totalCostWithoutInvesting: Number(c.totalCost) || 0,
      interestAtEnd: Number(c.interestAtEnd) || 0,
      riskLabel: 'Onbekend',
      finalInvestmentValue: Number(c.finalInvestmentValue) || 0,
      payoffByInvestment: Boolean(c.payoffByInvestment),
      enableDelay: Boolean(c.enableDelay),
      delayYears: Number(c.delayYears) || 0,
      delayMonths: Number(c.delayMonths) || 0,
      totalTermYears: Number(totalTermYears) || Number(c.planYears),
      enableGrowth: Boolean(c.enableGrowth),
      growthRate: c.enableGrowth ? (c.growthRate / 10).toFixed(1) : null,
      enableSeparateGrowth: Boolean(c.enableSeparateGrowth),
      investmentGrowthRate: c.enableSeparateGrowth ? (c.investmentGrowthRate / 10).toFixed(1) : null,
      chartData: c.chartDataMonthly || c.chartData || [],
      tableData: c.chartData || [],
      // Gebruik de opgeslagen strategie: die is direct beschikbaar; summaryCalc kan nog niet gesynchroniseerd zijn
      oneTimeRepayments: Array.isArray(loadedStrategy?.oneTimeRepayments) && loadedStrategy.oneTimeRepayments.length > 0
        ? loadedStrategy.oneTimeRepayments
        : (Array.isArray(c.oneTimeRepayments) ? c.oneTimeRepayments : [])
    };
    try {
      sessionStorage.setItem('sparroStrategyData', JSON.stringify(strategyData));
    } catch (e) {
      console.error('Error writing sessionStorage:', e);
    }
    setPendingNavigateToSummary(false);
    navigate('/summary', { state: { fromPortalSaved: true } });
  }, [pendingNavigateToSummary, loadedStrategy, user?.displayName, navigate, summaryCalc]);

  // Throttle hover updates (charts set sharedHoverYear themselves via PortalChart/PaymentChart)
  const handleHoverMove = useCallback((e) => {
    if (hoverTimeoutRef.current) {
      cancelAnimationFrame(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = requestAnimationFrame(() => {});
  }, []);

  const handleHoverLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      cancelAnimationFrame(hoverTimeoutRef.current);
    }
    setSharedHoverYear(null);
  }, []);

  // Growth rate: 0-50 representing 0.0% to 5.0% in 0.1% steps
  // growthRate value is divided by 1000 to get the decimal rate (e.g., 25 = 0.025 = 2.5%)
  const getGrowthRateValue = () => growthRate / 1000;
  const getInvestmentGrowthRateValue = () => {
    if (enableSeparateGrowth) {
      return investmentGrowthRate / 1000;
    }
    return growthRate / 1000;
  };

  const planYearsForDuo = Number(repaymentPlan) || 15;
  // DUO: 4% (35 jaar) of 12% (15 jaar) van inkomen boven draagkrachtvrije voet (€26.136 in 2026)
  const DUO_THRESHOLD = 26136;
  const duoPercentage = planYearsForDuo === 35 ? 0.04 : 0.12;
  const salaryBasedMinPayment = useMemo(() => {
    const above = yearlySalary - DUO_THRESHOLD;
    if (above <= 0) return 0;
    return Math.round((above * duoPercentage) / 12);
  }, [yearlySalary, duoPercentage]);

  const calculatedMinPayment = showSalary ? salaryBasedMinPayment : minPayment;

  // When using salary mode, keep minPayment in sync so simulation and "Voer bedrag direct in" show the right value
  useEffect(() => {
    if (showSalary) setMinPayment(salaryBasedMinPayment);
  }, [showSalary, salaryBasedMinPayment]);

  // Ensure monthly payment is never below minimum
  const effectiveMinPayment = calculatedMinPayment;
  const constrainedMonthlyPayment = Math.max(monthlyPayment, effectiveMinPayment);

  const planYears = Number(repaymentPlan) || 15;
  const yearsAlready = hasStartedRepaying ? Math.min(Math.max(0, yearsAlreadyRepaid), planYears - 1) : 0;
  const effectivePlanYears = Math.max(1, planYears - yearsAlready);

  // Calculate total term (stelsel + delay) - must be defined early as it's used in useMemo hooks
  const totalTermYears = effectivePlanYears + delayYears;
  const totalTermMonths = delayMonths;

  // DUO rentepercentages 2025: 2.21% voor 15 jaar, 2.57% voor 35 jaar (overruled by custom rates when enableCustomRates)
  const annualRate = getAnnualRateForPlan(planYears);
  const monthlyRate = annualRate / 12;
  const defaultRatePct = planYears === 15 ? 2.21 : 2.57;
  const customRatesSorted = useMemo(() => {
    if (!enableCustomRates || !customRates?.length) return [];
    return [...customRates]
      .filter((r) => r != null && typeof r.year === "number" && typeof r.ratePct === "number")
      .sort((a, b) => a.year - b.year);
  }, [enableCustomRates, customRates]);
  const getMonthlyRateForMonth = useCallback((monthIndex) => {
    if (customRatesSorted.length === 0) return monthlyRate;
    const y = monthIndex / 12;
    let seg = customRatesSorted[0];
    for (const s of customRatesSorted) {
      if (s.year <= y) seg = s;
      else break;
    }
    return (seg.ratePct / 100) / 12;
  }, [customRatesSorted, monthlyRate]);

  const gr = growthRate / 1000;
  const igr = enableSeparateGrowth ? investmentGrowthRate / 1000 : growthRate / 1000;

  const result = useMemo(
    () =>
      computePortalSchedule({
        totalDebt,
        constrainedMonthlyPayment,
        effectivePlanYears,
        totalTermYears,
        enableDelay,
        delayYears,
        delayMonths,
        enableGrowth,
        gr,
        igr,
        enableInvestment,
        monthlyInvestment,
        startingInvestmentCapital,
        oneTimeRepayments,
        getMonthlyRateForMonth,
      }),
    [
      totalDebt,
      constrainedMonthlyPayment,
      effectivePlanYears,
      totalTermYears,
      enableDelay,
      delayYears,
      delayMonths,
      enableGrowth,
      gr,
      igr,
      enableInvestment,
      monthlyInvestment,
      startingInvestmentCapital,
      oneTimeRepayments,
      getMonthlyRateForMonth,
    ]
  );

  const { schedule, payoffMonths, interestAtEnd, finalInvestmentValue, payoffByInvestment } = result;

  const yearlyData = useMemo(
    () =>
      buildPortalYearlyChartData({
        schedule,
        payoffMonths,
        effectivePlanYears,
        totalTermYears,
        totalTermMonths,
        enableDelay,
        delayYears,
        delayMonths,
        enableGrowth,
        gr,
        igr,
        enableInvestment,
        monthlyInvestment,
        startingInvestmentCapital,
        interestAtEnd,
        payoffByInvestment,
        constrainedMonthlyPayment,
      }),
    [
      schedule,
      payoffMonths,
      effectivePlanYears,
      totalTermYears,
      totalTermMonths,
      enableDelay,
      delayYears,
      delayMonths,
      enableGrowth,
      gr,
      igr,
      enableInvestment,
      monthlyInvestment,
      startingInvestmentCapital,
      interestAtEnd,
      payoffByInvestment,
      constrainedMonthlyPayment,
    ]
  );

  const yearlyTableData = useMemo(
    () =>
      buildPortalYearlyTableData({
        schedule,
        payoffMonths,
        effectivePlanYears,
        totalTermYears,
        totalDebt,
        planYears,
        enableDelay,
        delayYears,
        delayMonths,
        enableGrowth,
        gr,
        igr,
        enableInvestment,
        monthlyInvestment,
        startingInvestmentCapital,
        interestAtEnd,
        finalInvestmentValue,
        payoffByInvestment,
        constrainedMonthlyPayment,
      }),
    [
      schedule,
      payoffMonths,
      effectivePlanYears,
      totalTermYears,
      totalDebt,
      planYears,
      enableDelay,
      delayYears,
      delayMonths,
      enableGrowth,
      gr,
      igr,
      enableInvestment,
      monthlyInvestment,
      startingInvestmentCapital,
      interestAtEnd,
      finalInvestmentValue,
      payoffByInvestment,
      constrainedMonthlyPayment,
    ]
  );

  // Payment Chart Component  }, [schedule, payoffMonths, effectivePlanYears, interestAtEnd, finalInvestmentValue, payoffByInvestment, constrainedMonthlyPayment, enableGrowth, growthRate, enableSeparateGrowth, investmentGrowthRate, enableInvestment, monthlyInvestment, effectiveMinPayment, startingInvestmentCapital]);

  // Payment Chart Component – same width (900) and left/right padding (72) as PortalChart so x-axis aligns
  const PaymentChart = React.memo(({ data, hoverYear, setHoverYear }) => {
    const svgRef = useRef(null);
    const padding = { top: 24, right: 72, bottom: 36, left: 72 };
    const width = 900;
    const height = 120;

    // Use total term (stelsel + delay) for x-axis - define first before using it
    const maxX = enableDelay && (delayYears > 0 || delayMonths > 0)
      ? totalTermYears + (totalTermMonths > 0 ? totalTermMonths / 12 : 0)
      : planYears;
    const payoffYear = payoffMonths / 12;
    const maxYearToShow = payoffYear || maxX;

    // Calculate investment payments for each year
    const calculateInvestmentPayment = (targetYear) => {
      if (!enableInvestment) return 0;
      const delayMonthsTotal = enableDelay ? (delayYears * 12 + delayMonths) : 0;
      const delayYear = delayMonthsTotal / 12;
      
      // If before delay period, no investment payments
      if (targetYear < delayYear) return 0;
      
      // Calculate investment contribution with growth if enabled
      let investmentContribution = monthlyInvestment;
      if (enableGrowth) {
        const yearsElapsed = Math.max(0, targetYear - delayYear);
        const investmentGrowthRateValue = getInvestmentGrowthRateValue();
        investmentContribution = monthlyInvestment * Math.pow(1 + investmentGrowthRateValue, yearsElapsed);
      }
      
      // Get payment for this year to calculate available for investment
      const getPaymentAtYear = (yr) => {
        for (let i = 0; i < data.length; i++) {
          const pointYear = data[i].year;
          if (pointYear >= yr && pointYear < yr + 1) {
            return data[i].payment;
          }
        }
        for (let i = data.length - 1; i >= 0; i--) {
          if (data[i].year < yr) {
            return data[i].payment;
          }
        }
        return data[0]?.payment || 0;
      };
      
      const paymentForYear = getPaymentAtYear(targetYear);
      const availableForInvestment = Math.max(0, paymentForYear - effectiveMinPayment);
      return Math.min(investmentContribution, availableForInvestment);
    };

    // Calculate max Y including both payment and investment payments
    const maxPayment = Math.max(...data.map(d => d.payment), 1);
    const maxInvestmentPayment = enableInvestment ? Math.max(...Array.from({ length: Math.ceil(maxX) }, (_, i) => calculateInvestmentPayment(i)), 0) : 0;
    const maxY = Math.max(maxPayment, maxInvestmentPayment, 1);

    const x = (yr) => padding.left + (yr / maxX) * (width - padding.left - padding.right);
    const y = (v) => height - padding.bottom - (v / maxY) * (height - padding.top - padding.bottom);

    // Create investment payment path - stepped line (constant for each full year, then jumps at year boundaries)
    const investmentPaymentPath = enableInvestment && data.length > 1 ? (() => {
      const payoffYear = payoffMonths / 12;
      const dataToShow = payoffYear ? data.filter(d => d.year <= payoffYear) : data;
      
      if (dataToShow.length === 0) return "";
      
      const maxYearToUse = enableDelay && (delayYears > 0 || delayMonths > 0)
        ? totalTermYears + (totalTermMonths > 0 ? totalTermMonths / 12 : 0)
        : effectivePlanYears;
      const maxYear = Math.min(maxYearToUse, Math.ceil(payoffYear || maxYearToUse));
      
      let path = `M${x(0)},${y(calculateInvestmentPayment(0))}`;
      
      for (let year = 0; year < maxYear; year++) {
        const currentInvestmentPayment = calculateInvestmentPayment(year);
        const nextYear = year + 1;
        
        if (payoffYear && nextYear > payoffYear) {
          path += ` L${x(payoffYear)},${y(currentInvestmentPayment)}`;
          break;
        }
        
        const nextInvestmentPayment = calculateInvestmentPayment(nextYear);
        path += ` L${x(nextYear)},${y(currentInvestmentPayment)}`;
        path += ` L${x(nextYear)},${y(nextInvestmentPayment)}`;
      }
      
      if (payoffYear && payoffYear < maxYear && Math.floor(payoffYear) !== payoffYear) {
        const finalInvestmentPayment = calculateInvestmentPayment(Math.floor(payoffYear));
        path += ` L${x(payoffYear)},${y(finalInvestmentPayment)}`;
      }
      
      return path;
    })() : "";

    // Create payment path - stepped line (constant for each full year, then jumps at year boundaries)
    const paymentPath = data.length > 1 ? (() => {
      // Always use payoffMonths / 12 as the payoff year, regardless of how it was paid off
      const payoffYear = payoffMonths / 12;
      const dataToShow = payoffYear ? data.filter(d => d.year <= payoffYear) : data;
      
      if (dataToShow.length === 0) return "";
      
      // Get payment value for a given integer year
      // Since payment is constant for the whole year, find any point in that year
      const getPaymentAtYear = (targetYear) => {
        // Find a data point within this year (targetYear <= year < targetYear + 1)
        for (let i = 0; i < dataToShow.length; i++) {
          const pointYear = dataToShow[i].year;
          if (pointYear >= targetYear && pointYear < targetYear + 1) {
            return dataToShow[i].payment;
          }
        }
        // If not found in that year, find the last point before this year
        for (let i = dataToShow.length - 1; i >= 0; i--) {
          if (dataToShow[i].year < targetYear) {
            return dataToShow[i].payment;
          }
        }
        // Fallback to first point
        return dataToShow[0].payment;
      };
      
      // Find the maximum integer year to display
      const maxYearToUse = enableDelay && (delayYears > 0 || delayMonths > 0)
        ? totalTermYears + (totalTermMonths > 0 ? totalTermMonths / 12 : 0)
        : effectivePlanYears;
      const maxYear = Math.min(maxYearToUse, Math.ceil(payoffYear || maxYearToUse));
      
      let path = `M${x(0)},${y(getPaymentAtYear(0))}`;
      
      // Create stepped line: for each year, horizontal line, then vertical jump
      // Stop at payoff year
      for (let year = 0; year < maxYear; year++) {
        const currentPayment = getPaymentAtYear(year);
        const nextYear = year + 1;
        
        // Stop if we've reached or passed the payoff year
        if (payoffYear && nextYear > payoffYear) {
          // Draw to payoff point
          path += ` L${x(payoffYear)},${y(currentPayment)}`;
          break;
        }
        
        const nextPayment = getPaymentAtYear(nextYear);
        
        // Horizontal line across the year (stays at current payment)
        path += ` L${x(nextYear)},${y(currentPayment)}`;
        // Vertical jump to next payment value (at year boundary)
        path += ` L${x(nextYear)},${y(nextPayment)}`;
      }
      
      // Draw to final point if payoff year is not an integer and we haven't already drawn to it
      if (payoffYear && payoffYear < maxYear && Math.floor(payoffYear) !== payoffYear) {
        const finalPayment = getPaymentAtYear(Math.floor(payoffYear));
        path += ` L${x(payoffYear)},${y(finalPayment)}`;
      }
      
      return path;
    })() : "";

    // Find nearest point for hover: use shared hover year so indicator matches top chart
    const nearest = (() => {
      if (hoverYear == null) return null;
      const year = Math.max(0, Math.min(maxYearToShow, hoverYear));
      const dataToShow = payoffYear ? data.filter(d => d.year <= payoffYear) : data;
      const integerYear = Math.floor(Math.min(year, maxYearToShow));
      const getPaymentAtYear = (targetYear) => {
        for (let i = 0; i < dataToShow.length; i++) {
          const pointYear = dataToShow[i].year;
          if (pointYear >= targetYear && pointYear < targetYear + 1) {
            return dataToShow[i].payment;
          }
        }
        for (let i = dataToShow.length - 1; i >= 0; i--) {
          if (dataToShow[i].year < targetYear) {
            return dataToShow[i].payment;
          }
        }
        return dataToShow[0]?.payment || 0;
      };
      const payment = getPaymentAtYear(integerYear);
      const investmentPayment = enableInvestment ? calculateInvestmentPayment(integerYear) : 0;
      return { year: Math.min(year, maxYearToShow), payment, investmentPayment };
    })();

    const handlePaymentChartPointerMove = useCallback((e) => {
      const target = svgRef.current;
      if (!target || !setHoverYear) return;
      const rect = target.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      const svgX = rect.width > 0 ? localX * (width / rect.width) : localX;
      const year = Math.max(0, Math.min(maxYearToShow, ((svgX - padding.left) / (width - padding.left - padding.right)) * maxX));
      setHoverYear(year);
    }, [setHoverYear, maxYearToShow, padding, width]);
    const handlePaymentChartPointerLeave = useCallback(() => {
      if (setHoverYear) setHoverYear(null);
    }, [setHoverYear]);

    // Y-axis labels - dynamic based on actual payment data
    const yTicks = (() => {
      if (maxY <= 0) return [];
      
      // Calculate 3 evenly spaced intervals based on actual max payment value
      const step = maxY / 3;
      const intervals = [];
      
      for (let i = 1; i <= 3; i++) {
        intervals.push(Math.round(step * i));
      }
      
      return intervals;
    })();

    // X-axis ticks (same as main chart) - only show years in 5-year intervals
    const xTicks = [];
    for (let year = 0; year <= maxX; year += 5) {
      xTicks.push(year);
    }
    // Don't add final year if it's not in the 5-year interval - only show 5, 10, 15, 20, etc.

    return (
      <div style={{ width: "100%", height: "100%" }}>
        <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMinYMid meet" style={{ width: "100%", height: "auto" }}
          onMouseMove={handlePaymentChartPointerMove}
          onMouseLeave={handlePaymentChartPointerLeave}
        >
          {/* Axes */}
          <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} stroke="#CBD5E1" />
          <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} stroke="#CBD5E1" />
          
          {/* Axis labels */}
          <text x={padding.left} y={16} fill="#374151" fontSize="12">Euro</text>
          <text x={width - padding.right - 36} y={height - 8} fill="#374151" fontSize="12">Jaren</text>
          
          {/* Constant horizontal intervals */}
          {yTicks.map((t, i) => (
            <line key={`gy-${i}`} x1={padding.left} x2={width - padding.right} y1={y(t)} y2={y(t)} stroke="#f3f4f6" strokeWidth={1} />
          ))}
          
          {/* X-axis labels */}
          {xTicks.map((t, i) => (
            <text key={`tx-${i}`} x={x(t)} y={height - padding.bottom + 18} textAnchor="middle" fill="#6b7280" fontSize="12">{t}</text>
          ))}

          {/* Y-axis labels */}
          {yTicks.map((t, i) => (
            <text key={`ty-${i}`} x={padding.left - 8} y={y(t)} textAnchor="end" dominantBaseline="middle" fill="#6b7280" fontSize="12">{Math.round(t).toLocaleString('nl-NL')}</text>
          ))}

          {/* Payment line */}
          <path d={paymentPath} fill="none" stroke={PORTAL_ACCENT} strokeWidth={2} strokeLinecap="round" />
          
          {/* Investment payment line */}
          {enableInvestment && (
            <path d={investmentPaymentPath} fill="none" stroke="#10b981" strokeWidth={2} strokeLinecap="round" />
          )}
          
          {/* Hover line and point */}
          {nearest && (
            <g>
              <line x1={x(nearest.year)} x2={x(nearest.year)} y1={padding.top} y2={height - padding.bottom} stroke="#D1D5DB" />
              <circle cx={x(nearest.year)} cy={y(nearest.payment)} r={4} fill="#fff" stroke={PORTAL_ACCENT} strokeWidth={2} />
              {enableInvestment && nearest.investmentPayment > 0 && (
                <circle cx={x(nearest.year)} cy={y(nearest.investmentPayment)} r={4} fill="#fff" stroke="#10b981" strokeWidth={2} />
              )}
              <g>
                {(() => {
                  const tooltipY = Math.max(padding.top + 5, y(nearest.payment) - 30);
                  return (
                    <>
                      <rect x={x(nearest.year) - 26} y={tooltipY} rx={6} ry={6} width={52} height={18} fill={PORTAL_ACCENT} />
                      <text x={x(nearest.year)} y={tooltipY + 12} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="600">{Math.round(nearest.payment)}</text>
                    </>
                  );
                })()}
              </g>
              {enableInvestment && nearest.investmentPayment > 0 && (
                <g>
                  {(() => {
                    const tooltipY = Math.min(height - padding.bottom - 25, y(nearest.investmentPayment) + 20);
                    return (
                      <>
                        <rect x={x(nearest.year) - 26} y={tooltipY} rx={6} ry={6} width={52} height={18} fill="#10b981" />
                        <text x={x(nearest.year)} y={tooltipY + 12} textAnchor="middle" fill="#fff" fontSize="11" fontWeight="600">{Math.round(nearest.investmentPayment)}</text>
                      </>
                    );
                  })()}
                </g>
              )}
              {/* Year label on x-axis - only show if it's a 5-year interval */}
              {nearest.year % 5 === 0 && (
                <text 
                  x={x(nearest.year)} 
                  y={height - padding.bottom + 18} 
                  textAnchor="middle" 
                  fill="#6b7280" 
                  fontSize="12"
                >
                  {Math.round(nearest.year)}
                </text>
              )}
            </g>
          )}
        </svg>
      </div>
    );
  });

  // Total cost = som van werkelijke maandelijkse aflossingen uit de schedule (incl. laatste maand ≤ betaling)
  const totalCost = useMemo(() => {
    if (schedule.length < 2) return 0;
    const fractionalMonth = payoffMonths - Math.floor(payoffMonths);
    return schedule.reduce((sum, row, i) => {
      const pay = row.loanPayment ?? 0;
      if (fractionalMonth > 0 && i === schedule.length - 1) return sum + pay * fractionalMonth;
      return sum + pay;
    }, 0);
  }, [schedule, payoffMonths]);

  // Total investment payments = som van werkelijke maandelijkse investeringen uit de schedule (laatste maand 0 of deel)
  const totalInvestmentPayments = useMemo(() => {
    if (!enableInvestment || monthlyInvestment <= 0) return 0;
    const fractionalMonth = payoffMonths - Math.floor(payoffMonths);
    return schedule.reduce((sum, row, i) => {
      const contrib = row.investmentContribution ?? 0;
      if (fractionalMonth > 0 && i === schedule.length - 1) return sum + contrib * fractionalMonth;
      return sum + contrib;
    }, 0);
  }, [enableInvestment, monthlyInvestment, schedule, payoffMonths]);
  
  // Total cost including investment payments (totale kosten = loan payments + investment payments)
  const totalOneTimeRepayments = useMemo(() => {
    const yearsApplied = Math.max(0, Math.floor(payoffMonths / 12));
    return (oneTimeRepayments || [])
      .filter((r) => r && (r.amount || 0) > 0)
      .reduce((sum, r) => sum + (r.year === 0 ? (Number(r.amount) || 0) * yearsApplied : (Number(r.amount) || 0)), 0);
  }, [oneTimeRepayments, payoffMonths]);
  const totalCostWithInvestments = useMemo(() => {
    return totalCost + totalInvestmentPayments + totalOneTimeRepayments;
  }, [totalCost, totalInvestmentPayments, totalOneTimeRepayments]);
  
  // Calculate what total cost would be WITHOUT investing (keeping same monthly payment)
  const totalCostWithoutInvesting = useMemo(() => {
    if (!enableInvestment) return totalCost; // If not investing, same as current
    
    // Run the same calculation but with monthlyInvestment = 0
    // Calculate total cost as sum of all payments (matching totalCost calculation)
    let balance = Math.max(0, Number(totalDebt) || 0);
    const basePay = Math.max(0, Number(constrainedMonthlyPayment) || 0);
    
    if (basePay <= 0 || balance <= 0) return 0;
    
    let m = 0;
    // Use total term (stelsel + delay) for max months if delay is enabled
    const maxTermMonthsForCost = enableDelay && (delayYears > 0 || delayMonths > 0)
      ? (totalTermYears * 12 + totalTermMonths)
      : effectivePlanYears * 12;
    const maxMonths = maxTermMonthsForCost;
    let totalPaid = 0;
    
    while (balance > 0 && m < maxMonths) {
      // Calculate payment with growth if enabled
      let currentPay = basePay;
      if (enableGrowth) {
        const yearsElapsed = Math.floor(m / 12);
        const growthRateValue = getGrowthRateValue();
        currentPay = basePay * Math.pow(1 + growthRateValue, yearsElapsed);
      }
      
      // All payment goes to loan (no investment)
      // Calculate monthly interest: Principal at start of month × (APR / 12)
      const monthlyInterest = balance * getMonthlyRateForMonth(m - 1);
      // Interest is paid first, remainder goes to principal
      const principalPortion = currentPay - monthlyInterest;
      balance = Math.max(0, balance - principalPortion);
      totalPaid += currentPay;
      m += 1;
      
      if (balance <= 0) break;
    }
    
    // Total cost = sum of all payments made
    return totalPaid;
  }, [totalDebt, constrainedMonthlyPayment, effectivePlanYears, getMonthlyRateForMonth, enableGrowth, growthRate, enableSeparateGrowth, investmentGrowthRate, enableInvestment, totalCost]);
  
  // Calculate added value of investments (negative delta = positive value, positive delta = negative value)
  const addedInvestmentValue = enableInvestment ? 
    (totalCostWithoutInvesting - totalCost) : 0;
  
  const riskRatio = Math.min(1, (payoffMonths / (effectivePlanYears * 12 || 1)));
  const riskLabel = riskRatio < 0.33 ? "Laag" : riskRatio < 0.67 ? "Midden" : "Hoog";
  const payoffYears = Math.floor(payoffMonths / 12);
  const payoffRemainderMonths = Math.round(payoffMonths - payoffYears * 12);
  const netWorth = enableInvestment ? (finalInvestmentValue || 0) - (result.interestAtEnd || 0) : 0;

  // Calculate payment required to match exact term (15 or 35 years)
  const calculatePaymentForTerm = () => {
    // Always use effectivePlanYears (remaining term) as the target term
    // The loan should be paid off within effectivePlanYears after the delay period
    // Calculate delay period in months
    const delayMonthsTotal = enableDelay ? (delayYears * 12 + delayMonths) : 0;
    // Total target months = delay + effectivePlanYears (loan must be paid off within remaining term after delay)
    const targetMonths = delayMonthsTotal + (effectivePlanYears * 12);
    // Repayment period is exactly effectivePlanYears months (after delay)
    const repaymentMonths = effectivePlanYears * 12;
    
    // Helper function to simulate loan and return balance at targetMonths
    const simulateToTarget = (testPayment) => {
      let balance = Number(totalDebt) || 0;
      const startingCapital = Number(startingInvestmentCapital) || 0;
      let investmentValue = startingCapital;
      // Monthly investment rate = Annual rate / 12 (simple division method, same as loan interest)
      const monthlyInvestmentRate = 0.06 / 12;
      
      // Delay period: only interest accrues, loan balance grows
      if (delayMonthsTotal > 0) {
        for (let delayMonth = 0; delayMonth < delayMonthsTotal; delayMonth++) {
          const monthlyInterest = balance * getMonthlyRateForMonth(delayMonth);
          // Interest is added to principal (loan grows during delay period)
          balance = balance + monthlyInterest;
          
          // Investments can still grow during delay period if enabled
          if (enableInvestment && investmentValue > 0) {
            investmentValue = investmentValue * (1 + monthlyInvestmentRate);
          }
        }
      }
      
      // Start repayment after delay period
      const repaymentStartMonth = delayMonthsTotal;
      
      // Simulate repayment period
      for (let m = 0; m < repaymentMonths; m++) {
        const currentMonth = repaymentStartMonth + m;
        // Calculate payment with growth if enabled
        let currentPay = testPayment;
        if (enableGrowth) {
          // Calculate years elapsed since delay period ended (not since month 0)
          // Use the repayment month index (m) divided by 12, not currentMonth
          const yearsElapsed = Math.floor(m / 12);
          const growthRateValue = getGrowthRateValue();
          currentPay = testPayment * Math.pow(1 + growthRateValue, yearsElapsed);
        }
        
        // Calculate investment contribution (separate from loan payment)
        // Investments are now completely separate - they don't affect loan payments
        let investmentAmount = 0;
        if (enableInvestment) {
          // Calculate investment contribution with growth if enabled
          let investmentContribution = monthlyInvestment;
          if (enableGrowth) {
            const yearsElapsed = Math.floor(m / 12);
            const investmentGrowthRateValue = getInvestmentGrowthRateValue();
            investmentContribution = monthlyInvestment * Math.pow(1 + investmentGrowthRateValue, Math.max(0, yearsElapsed));
          }
          investmentAmount = investmentContribution;
        }
        // Loan payment is always the full payment (investments don't reduce it)
        const loanPayment = currentPay;
        
        // Order of operations (EXACT same as main calculation):
        // Nieuwe methodologie: eerst groeien, dan inleg toevoegen
        // E_maand_nieuw = E_maand_oud × (1 + r_maand) + I_m
        investmentValue = investmentValue * (1 + monthlyInvestmentRate) + investmentAmount;
        // 3. Calculate monthly interest: Principal at start of month × (APR / 12)
        const monthlyInterest = balance * getMonthlyRateForMonth(currentMonth);
        // 4. Interest is paid first from loan payment, remainder goes to principal
        const principalPortion = loanPayment - monthlyInterest;
        balance = Math.max(0, balance - principalPortion);
        
        // Check if we've reached targetMonths
        const monthAfterThis = currentMonth + 1;
        if (monthAfterThis >= targetMonths) {
          // We've reached or passed targetMonths, check if we can pay off
          if (enableInvestment && investmentValue >= balance && balance > 0) {
            // Investments can pay off the loan at targetMonths - perfect match!
            return { balance: 0, month: targetMonths, paidOffEarly: false, paidOffByInvestment: true };
          }
          if (balance <= 0.01) {
            // Paid off exactly at targetMonths
            return { balance: 0, month: targetMonths, paidOffEarly: false, paidOffByInvestment: false };
          }
          // Continue to end of loop to return final state
          break;
        }
        
        // Check if investment can pay off loan before targetMonths
        if (enableInvestment && investmentValue >= balance && balance > 0) {
          // Investment pays off loan - this is too early
          return { balance: 0, month: monthAfterThis, paidOffEarly: true, paidOffByInvestment: true };
        }
        
        // If balance becomes zero before targetMonths, we're paying too much
        if (balance <= 0) {
          return { balance: 0, month: monthAfterThis, paidOffEarly: true, paidOffByInvestment: false };
        }
      }
      
      // After simulating to targetMonths, check if investments can pay off remaining balance
      // Calculate the final month index (repaymentStartMonth + repaymentMonths - 1)
      const finalMonth = repaymentStartMonth + repaymentMonths - 1;
      
      if (enableInvestment && investmentValue >= balance && balance > 0) {
        // Investments can pay off the loan at targetMonths - perfect match!
        return { balance: 0, month: targetMonths, paidOffEarly: false, paidOffByInvestment: true };
      }
      
      // Also check if balance is already 0 (paid off exactly at targetMonths)
      if (balance <= 0.01) {
        return { balance: 0, month: targetMonths, paidOffEarly: false, paidOffByInvestment: false };
      }
      
      return { balance, month: targetMonths, paidOffEarly: false, paidOffByInvestment: false };
    };
    
    // Binary search - find payment that results in payoff at or before targetMonths
    let low = effectiveMinPayment;
    let high = 10000;
    let bestPayment = null;
    let bestResult = null;
    
    for (let iter = 0; iter < 150; iter++) {
      const testPayment = (low + high) / 2;
      const result = simulateToTarget(testPayment);
      
      // If balance is exactly 0 at targetMonths (either by direct payment or investment), we found it!
      if ((Math.abs(result.balance) < 0.01 || result.paidOffByInvestment) && !result.paidOffEarly) {
        return testPayment;
      }
      
      // Track the best payment that results in payoff within term
      if (!result.paidOffEarly && (result.balance <= 0.01 || result.paidOffByInvestment)) {
        if (!bestPayment || Math.abs(result.month - targetMonths) < Math.abs(bestResult.month - targetMonths)) {
          bestPayment = testPayment;
          bestResult = result;
        }
      }
      
      // Adjust search bounds
      if (result.paidOffEarly) {
        // Paid off too early (before targetMonths), need to pay LESS to take longer
        // This applies whether paid off by investment or direct payment
        high = testPayment;
      } else if (result.paidOffByInvestment && result.balance <= 0.01) {
        // Investments can pay off the loan at targetMonths - perfect match!
        // This should have been caught above, but if we get here, it's still good
        return testPayment;
      } else if (result.balance > 0.01 && !result.paidOffByInvestment) {
        // Not paid off by targetMonths and investments can't cover it, need to pay MORE to pay off faster
        low = testPayment;
      } else if (result.paidOffByInvestment && result.balance > 0.01) {
        // Investments can pay off but haven't yet - this shouldn't happen if logic is correct
        // But if it does, we need to pay MORE to increase investments faster
        low = testPayment;
      } else {
        // Balance is exactly 0 at targetMonths - perfect! (should have returned above)
        // But if we get here, balance is slightly negative, so pay LESS
        high = testPayment;
      }
      
      // Convergence check
      if (high - low < 0.01) {
        break;
      }
    }
    
    // If we found a good payment during search, use it
    if (bestPayment && bestResult && !bestResult.paidOffEarly) {
      return bestPayment;
    }
    
    // Otherwise, ensure we return a payment that results in payoff within term
    // Test the final payment to make sure it works
    const finalPayment = (low + high) / 2;
    const finalResult = simulateToTarget(finalPayment);
    
    // If it results in payoff at or before targetMonths (not after), use it
    if (!finalResult.paidOffEarly && (finalResult.balance <= 0.01 || finalResult.paidOffByInvestment)) {
      return finalPayment;
    }
    
    // If it paid off too early, that's also acceptable (we'll use it)
    if (finalResult.paidOffEarly) {
      return finalPayment;
    }
    
    // If still not paid off within term, find minimum payment that results in payoff
    // Search with 1 euro intervals for more precision
    let payment = finalPayment;
    for (let i = 0; i < 500; i++) {
      payment += 1;
      const testResult = simulateToTarget(payment);
      // Accept if paid off at or before targetMonths
      if (!testResult.paidOffEarly && (testResult.balance <= 0.01 || testResult.paidOffByInvestment)) {
        return payment;
      }
      // Also accept if paid off early (better than not paid off)
      if (testResult.paidOffEarly) {
        return payment;
      }
    }
    
    // Fallback: return a high payment that should definitely work
    return Math.max(finalPayment, 5000);
  };

  const handleMatchTerm = () => {
    const calculatedPayment = calculatePaymentForTerm();
    setMonthlyPayment(Math.round(calculatedPayment * 100) / 100);
  };

  const openSaveStrategyModal = () => {
    setSaveNameValue('Strategie ' + new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }));
    setShowSaveNameModal(true);
  };

  const doSaveStrategy = async (name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return;
    if (isSavingStrategy) return;
    setIsSavingStrategy(true);
    setSaveMessage(null);

    const userData = {
      userId: user?.uid || 'anonymous',
      userEmail: user?.email || 'unknown',
      userName: user?.displayName || 'Unknown User',
      totalDebt: Number(totalDebt) || 0,
      repaymentPlan: Number(planYears) || 15,
      minPayment: Number(effectiveMinPayment) || 0,
      monthlyPayment: Number(constrainedMonthlyPayment) || 0,
      enableGrowth: Boolean(enableGrowth),
      growthRate: enableGrowth ? `${(growthRate / 10).toFixed(1)}%` : null,
      yearlySalary: showSalary ? Number(yearlySalary) : null,
      calculatedMinPayment: Number(calculatedMinPayment) || 0,
      totalCost: Number(totalCost) || 0,
      interestAtEnd: Number(interestAtEnd) || 0,
      payoffMonths: Number(payoffMonths) || 0,
      payoffYears: Number(payoffYears) || 0,
      payoffRemainderMonths: Number(payoffRemainderMonths) || 0,
      riskLabel: String(riskLabel) || 'Onbekend',
      enableInvestment: Boolean(enableInvestment),
      monthlyInvestment: Number(monthlyInvestment) || 0,
      startingInvestmentCapital: Number(startingInvestmentCapital) || 0,
      finalInvestmentValue: Number(finalInvestmentValue) || 0,
      payoffByInvestment: Boolean(payoffByInvestment),
      addedInvestmentValue: Number(addedInvestmentValue) || 0,
      annualRate: Number(getAnnualRateForPlan(planYears))
    };
    saveUserData(userData);

    const strategyPayload = {
      name: trimmed,
      totalDebt: Number(totalDebt) || 0,
      repaymentPlan: String(planYears) || '15',
      hasStartedRepaying: Boolean(hasStartedRepaying),
      yearsAlreadyRepaid: Number(yearsAlreadyRepaid) || 0,
      oneTimeRepayments: (oneTimeRepayments || []).filter((r) => r && (r.amount || 0) > 0).length > 0
        ? oneTimeRepayments
        : [],
      minPayment: Number(effectiveMinPayment) || 0,
      monthlyPayment: Number(constrainedMonthlyPayment) || 0,
      enableGrowth: Boolean(enableGrowth),
      growthRate: growthRate,
      enableSeparateGrowth: Boolean(enableSeparateGrowth),
      investmentGrowthRate: investmentGrowthRate,
      enableInvestment: Boolean(enableInvestment),
      monthlyInvestment: Number(monthlyInvestment) || 0,
      enableDelay: Boolean(enableDelay),
      delayYears: Number(delayYears) || 0,
      delayMonths: Number(delayMonths) || 0,
      startingInvestmentCapital: Number(startingInvestmentCapital) || 0,
      enableCustomRates: Boolean(enableCustomRates),
      customRates: Array.isArray(customRates) && customRates.length > 0 ? customRates : [],
    };

    try {
      const ok = await savePortalStrategy(strategyPayload, uid);
      if (!ok.success) {
        setSaveMessage("Opslaan mislukt. " + (ok.error || "Probeer het opnieuw."));
        return;
      }
      const timestamp = new Date().toISOString();
      const saved = {
        id: ok.id,
        timestamp,
        name: strategyPayload.name,
        totalDebt: strategyPayload.totalDebt,
        repaymentPlan: strategyPayload.repaymentPlan,
        hasStartedRepaying: strategyPayload.hasStartedRepaying,
        yearsAlreadyRepaid: strategyPayload.yearsAlreadyRepaid,
        oneTimeRepayments: strategyPayload.oneTimeRepayments || [],
        minPayment: strategyPayload.minPayment,
        monthlyPayment: strategyPayload.monthlyPayment,
        enableGrowth: strategyPayload.enableGrowth,
        growthRate: strategyPayload.growthRate,
        enableSeparateGrowth: strategyPayload.enableSeparateGrowth,
        investmentGrowthRate: strategyPayload.investmentGrowthRate,
        enableInvestment: strategyPayload.enableInvestment,
        monthlyInvestment: strategyPayload.monthlyInvestment,
        enableDelay: strategyPayload.enableDelay,
        delayYears: strategyPayload.delayYears,
        delayMonths: strategyPayload.delayMonths,
        startingInvestmentCapital: strategyPayload.startingInvestmentCapital ?? 0,
        enableCustomRates: strategyPayload.enableCustomRates,
        customRates: strategyPayload.customRates || [],
      };
      setSavedStrategies((prev) => [saved, ...prev]);
      setLoadedStrategy(saved);
      setShowSaveNameModal(false);
      // PC: navigate to original SummaryPage (not in-app mobile-style summary)
      const strategyData = {
        userName: user?.displayName || 'Unknown User',
        totalDebt: Number(totalDebt) || 0,
        repaymentSystem: Number(planYears) || 15,
        minPayment: Number(effectiveMinPayment) || 0,
        monthlyPayment: Number(constrainedMonthlyPayment) || 0,
        enableInvestment: Boolean(enableInvestment),
        monthlyInvestment: Number(monthlyInvestment) || 0,
        startingInvestmentCapital: Number(startingInvestmentCapital) || 0,
        payoffMonths: Number(payoffMonths) || 0,
        totalCost: Number(totalCostWithInvestments) || 0,
        totalCostWithoutInvesting: Number(totalCostWithoutInvesting) || 0,
        interestAtEnd: Number(interestAtEnd) || 0,
        riskLabel: String(riskLabel) || 'Onbekend',
        finalInvestmentValue: Number(finalInvestmentValue) || 0,
        payoffByInvestment: Boolean(payoffByInvestment),
        enableDelay: Boolean(enableDelay),
        delayYears: Number(delayYears) || 0,
        delayMonths: Number(delayMonths) || 0,
        totalTermYears: Number(totalTermYears) || Number(planYears),
        enableGrowth: Boolean(enableGrowth),
        growthRate: enableGrowth ? (growthRate / 10).toFixed(1) : null,
        enableSeparateGrowth: Boolean(enableSeparateGrowth),
        investmentGrowthRate: enableSeparateGrowth ? (investmentGrowthRate / 10).toFixed(1) : null,
        chartData: yearlyData || [],
        tableData: yearlyTableData || [],
        oneTimeRepayments: Array.isArray(oneTimeRepayments) ? oneTimeRepayments : []
      };
      try {
        sessionStorage.setItem('sparroStrategyData', JSON.stringify(strategyData));
      } catch (e) {
        console.error('Error writing sessionStorage:', e);
      }
      navigate('/summary');
    } catch (err) {
      console.error("Save strategy error:", err);
      setSaveMessage("Opslaan mislukt. Probeer het opnieuw.");
    } finally {
      setIsSavingStrategy(false);
    }

    try {
      logUserActivity('result', {
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'unknown',
        userName: user?.displayName || 'Unknown User',
        totalDebt: Number(totalDebt) || 0,
        repaymentSystem: Number(planYears) || 15,
        minPayment: Number(effectiveMinPayment) || 0,
        monthlyPayment: Number(constrainedMonthlyPayment) || 0,
        enableGrowth: Boolean(enableGrowth),
        growthRate: enableGrowth ? `${(growthRate / 10).toFixed(1)}%` : null,
        enableInvestment: Boolean(enableInvestment),
        monthlyInvestment: Number(monthlyInvestment) || 0,
        payoffMonths: Number(payoffMonths) || 0,
        totalCost: Number(totalCostWithInvestments) || 0,
        interestAtEnd: Number(interestAtEnd) || 0,
        payoffByInvestment: Boolean(payoffByInvestment)
      });
    } catch (e) {
      console.warn('Failed to log result to analytics', e);
    }
  };


  // Determine which sections should be blurred
  const shouldBlurAll = guideStep === 'welcome';
  const shouldBlurRight = isGuideSidebarInputPhase;

  // When returning from summary (Terug naar opgeslagen strategieën), open saved list
  useEffect(() => {
    if (location.state?.openSaved) {
      setMainView('saved');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openSaved, location.pathname, navigate]);

  useEffect(() => {
    document.title = "Slimmer af - Portal";
    
    // Restore strategy data from sessionStorage if available
    try {
      const savedStrategyData = sessionStorage.getItem('sparroStrategyData');
      if (savedStrategyData) {
        const strategyData = JSON.parse(savedStrategyData);
        
        // Restore form fields from saved strategy
        if (strategyData.totalDebt !== undefined) {
          setTotalDebt(strategyData.totalDebt);
        }
        if (strategyData.repaymentSystem !== undefined) {
          setRepaymentPlan(String(strategyData.repaymentSystem));
        }
        if (strategyData.minPayment !== undefined) {
          setMinPayment(strategyData.minPayment);
        }
        if (strategyData.monthlyPayment !== undefined) {
          setMonthlyPayment(strategyData.monthlyPayment);
        }
        if (strategyData.enableInvestment !== undefined) {
          setEnableInvestment(strategyData.enableInvestment);
        }
        if (strategyData.monthlyInvestment !== undefined) {
          setMonthlyInvestment(strategyData.monthlyInvestment);
        } else if (strategyData.investmentPercentage !== undefined) {
          // Legacy support: convert percentage to amount if old data format
          const availableForInvestment = Math.max(0, (strategyData.monthlyPayment || 0) - (strategyData.minPayment || 0));
          setMonthlyInvestment(availableForInvestment * (strategyData.investmentPercentage / 100));
        }
        if (strategyData.startingInvestmentCapital !== undefined) {
          setStartingInvestmentCapital(strategyData.startingInvestmentCapital > 0 ? String(strategyData.startingInvestmentCapital) : "");
        }
        if (strategyData.enableDelay !== undefined) {
          setEnableDelay(strategyData.enableDelay);
        }
        if (strategyData.delayYears !== undefined) {
          setDelayYears(strategyData.delayYears);
        }
        if (strategyData.delayMonths !== undefined) {
          setDelayMonths(strategyData.delayMonths);
        }
      }
    } catch (e) {
      console.warn('Failed to restore strategy data from sessionStorage', e);
    }
  }, []);

  // Measure right area container width and calculate chart width
  const rightAreaRefCallback = useCallback((node) => {
    if (node) {
      rightAreaRef.current = node;
      // Right area has padding: 24px on each side = 48px total
      // Chart containers have padding: 16px on each side = 32px total each
      // So available width = rightArea width - 48px (right area padding) - 32px (chart container padding)
      const rightAreaPadding = 24 * 2; // 48px total
      const chartContainerPadding = 16 * 2; // 32px total
      const measuredWidth = node.offsetWidth - rightAreaPadding - chartContainerPadding;
      if (measuredWidth > 0) {
        setChartWidth(measuredWidth);
      }
    }
  }, []);

  // Measure right area width on resize to ensure both charts stay aligned
  useEffect(() => {
    const updateChartWidth = () => {
      if (rightAreaRef.current) {
        const rightAreaPadding = 24 * 2; // 48px total
        const chartContainerPadding = 16 * 2; // 32px total
        const measuredWidth = rightAreaRef.current.offsetWidth - rightAreaPadding - chartContainerPadding;
        if (measuredWidth > 0 && measuredWidth !== chartWidth) {
          setChartWidth(measuredWidth);
        }
      }
    };
    
    // Use a small delay to ensure DOM is updated
    const timeoutId = setTimeout(updateChartWidth, 10);
    window.addEventListener('resize', updateChartWidth);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateChartWidth);
    };
  }, [chartWidth]);

  return (
    <div style={{ background: bg, height: "100vh", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", padding: "0px", position: "relative" }}>
      {/* Welcome Modal */}
      {guideStep === 'welcome' && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(8px)"
        }}>
          <div style={{
            background: "#FFFFFF",
            borderRadius: 24,
            padding: "48px 40px",
            maxWidth: 560,
            width: "90%",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            textAlign: "center",
            position: "relative"
          }}>
            <h1 style={{ 
              marginTop: 0, 
              marginBottom: 16, 
              fontSize: 32, 
              fontWeight: 700, 
              color: "#111827",
              lineHeight: 1.2
            }}>
              Welkom bij de Slimmer af portal
            </h1>
            <p style={{ 
              marginBottom: 32, 
              fontSize: 18, 
              color: "#4B5563", 
              lineHeight: 1.6 
            }}>
              We nemen je kort mee door de portal. Je kunt dit overslaan en later altijd terugkomen.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => {
                  setGuideStep('completed');
                  localStorage.setItem('sparro-guide-completed', 'true');
                }}
                style={{
                  padding: "14px 28px",
                  borderRadius: 12,
                  border: "2px solid #D1D5DB",
                  background: "#FFFFFF",
                  color: "#374151",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#F9FAFB";
                  e.target.style.borderColor = "#9CA3AF";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "#FFFFFF";
                  e.target.style.borderColor = "#D1D5DB";
                }}
              >
                Skip
              </button>
              <button
                onClick={handleStartGuide}
                style={{
                  padding: "14px 28px",
                  borderRadius: 12,
                  border: "none",
                  ...PORTAL_GRADIENT,
                  color: "#FFFFFF",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 12px rgba(83, 113, 255, 0.4)",
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 16px rgba(83, 113, 255, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 12px rgba(83, 113, 255, 0.4)";
                }}
              >
                Beginnen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save name modal (same flow as mobile) */}
      {showSaveNameModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => !isSavingStrategy && setShowSaveNameModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 24,
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1f2937", marginBottom: 16 }}>Strategie opslaan</div>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 12 }}>Geef deze strategie een naam:</p>
            <input
              type="text"
              value={saveNameValue}
              onChange={(e) => setSaveNameValue(e.target.value)}
              placeholder="Bijv. Mijn aflosplan"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && doSaveStrategy(saveNameValue)}
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "12px 14px",
                fontSize: 16,
                border: "2px solid #e5e7eb",
                borderRadius: 10,
                marginBottom: 20,
              }}
            />
            {saveMessage && (
              <div style={{ marginBottom: 16, fontSize: 14, color: saveMessage.includes("mislukt") ? "#dc2626" : "#059669" }}>{saveMessage}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => !isSavingStrategy && setShowSaveNameModal(false)}
                style={{ padding: "10px 20px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 15, fontWeight: 600, cursor: "pointer" }}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => doSaveStrategy(saveNameValue)}
                disabled={isSavingStrategy || !saveNameValue.trim()}
                style={{ padding: "10px 20px", borderRadius: 10, border: "none", ...PORTAL_GRADIENT, color: "#fff", fontSize: 15, fontWeight: 600, cursor: isSavingStrategy ? "wait" : "pointer", opacity: isSavingStrategy || !saveNameValue.trim() ? 0.7 : 1 }}
              >
                {isSavingStrategy ? "Opslaan…" : "Opslaan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global blur overlay during welcome */}
      {shouldBlurAll && (
        <div style={{
          position: "absolute",
          inset: 0,
          background: "rgba(255, 255, 255, 0.3)",
          backdropFilter: "blur(8px)",
          zIndex: 9999,
          pointerEvents: "none"
        }} />
      )}

      {/* Opgeslagen strategieën view (legacy overlay, only when not using unified layout). */}
      {!USE_UNIFIED_TOP_BAR && mainView === "saved" && (
        <div style={{
          position: "absolute",
          ...(USE_UNIFIED_TOP_BAR ? { top: TOP_BAR_HEIGHT, left: 0, right: 0, bottom: 0 } : { inset: 0 }),
          display: "flex",
          flexDirection: "column",
          zIndex: 1,
          background: bg
        }}>
          {!USE_UNIFIED_TOP_BAR && (
          <header style={{
            flexShrink: 0,
            height: TOP_BAR_HEIGHT,
            background: "#fff",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
          }}>
            <a href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
              <img src="/logo-name.svg" alt="Slimmer af" style={{ height: 36, width: "auto", display: "block" }} />
            </a>
            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <button type="button" onClick={openSaveStrategyModal} disabled={isSavingStrategy}
                style={{
                  padding: "10px 20px",
                  borderRadius: 9999,
                  border: "none",
                  ...isSavingStrategy ? { background: "#e5e7eb" } : PORTAL_GRADIENT,
                  color: isSavingStrategy ? "#9ca3af" : "#FFFFFF",
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: isSavingStrategy ? "not-allowed" : "pointer",
                  boxShadow: isSavingStrategy ? "none" : "0 2px 8px rgba(51,153,255,0.3)",
                  transition: "all 0.2s ease"
                }}
              >
                {isSavingStrategy ? "Bezig met opslaan…" : "Sla strategie op"}
              </button>
              <div style={{ width: 1, height: 24, background: "#e5e7eb", margin: "0 20px", flexShrink: 0 }} aria-hidden />
              <nav style={{ display: "flex", alignItems: "center", gap: "clamp(12px, 2vw, 24px)" }}>
                <button type="button" onClick={() => setMainView("portal")}
                  style={{
                    padding: "8px 0",
                    borderRadius: 0,
                    border: "none",
                    background: "transparent",
                    color: "#4b5563",
                    fontSize: 15,
                    fontWeight: 500,
                    cursor: "pointer",
                    textDecoration: "none",
                    transition: "all 0.2s ease"
                  }}
                >
                  Strategie samenstellen
                </button>
                <button type="button"
                  style={{
                    padding: "10px 20px",
                    borderRadius: 9999,
                    border: "none",
                    ...PORTAL_GRADIENT,
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(51,153,255,0.3)"
                  }}
                >
                  Opgeslagen strategieën
                </button>
                <a href="/#faq" style={{ padding: "8px 0", textDecoration: "none", background: "transparent", color: "#4b5563", fontSize: 15, fontWeight: 500, transition: "all 0.2s ease" }}>
                  FAQ
                </a>
              </nav>
            </div>
          </header>
          )}
          <div style={{ flex: 1, overflowY: "auto" }}>
          <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
            <h1 style={{ margin: 0, marginBottom: 24, fontSize: 24, fontWeight: 700, color: "#111827" }}>Opgeslagen strategieën</h1>
            {savedStrategies.length === 0 ? (
              <p style={{ color: "#6b7280", fontSize: 15 }}>Nog geen strategieën opgeslagen. Sla je huidige instellingen op via &quot;Sla strategie op&quot;.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {savedStrategies.map((s) => (
                  <li
                    key={s.id}
                    style={{
                      padding: "20px 24px",
                      background: "#f9fafb",
                      borderRadius: 16,
                      marginBottom: 16,
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                      <div>
                        <div style={{ fontWeight: 600, color: "#1f2937", fontSize: 17 }}>{s.name}</div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                          {s.timestamp ? new Date(s.timestamp).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => {
                            setLoadedStrategy(s);
                            setPendingNavigateToSummary(true);
                          }}
                          style={{
                            padding: "10px 18px",
                            borderRadius: 10,
                            border: "none",
                            background: "#374151",
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Samenvatting & aflosschema
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setLoadedStrategy(s);
                            setTotalDebt(s.totalDebt ?? 23000);
                            setRepaymentPlan(String(s.repaymentPlan ?? 15));
                            setMinPayment(s.minPayment ?? 0);
                            setMonthlyPayment(s.monthlyPayment ?? 250);
                            setEnableGrowth(!!s.enableGrowth);
                            setGrowthRate(s.growthRate ?? 0);
                            setEnableSeparateGrowth(!!s.enableSeparateGrowth);
                            setInvestmentGrowthRate(s.investmentGrowthRate ?? 0);
                            setEnableInvestment(!!s.enableInvestment);
                            setMonthlyInvestment(s.monthlyInvestment ?? 0);
                            setEnableDelay(!!s.enableDelay);
                            setDelayYears(s.delayYears ?? 0);
                            setDelayMonths(s.delayMonths ?? 0);
                            setStartingInvestmentCapital(s.startingInvestmentCapital ? String(s.startingInvestmentCapital) : "");
                            setHasStartedRepaying(!!s.hasStartedRepaying);
                            setYearsAlreadyRepaid(s.yearsAlreadyRepaid ?? 0);
                            setOneTimeRepayments(Array.isArray(s.oneTimeRepayments) && s.oneTimeRepayments.length > 0 ? s.oneTimeRepayments : [{ amount: 0, year: 1 }]);
                            setEnableCustomRates(!!s.enableCustomRates);
                            setCustomRates(Array.isArray(s.customRates) && s.customRates.length > 0 ? s.customRates : []);
                            setMainView("portal");
                          }}
                          style={{
                            padding: "10px 18px",
                            borderRadius: 10,
                            border: "none",
                            ...PORTAL_GRADIENT,
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Laad in portal
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (window.confirm(`Weet je zeker dat je "${s.name}" wilt verwijderen?`)) {
                          await deletePortalStrategy(s.id, uid);
                          const list = await getSavedStrategies(uid);
                          setSavedStrategies(list);
                        }
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        fontSize: 13,
                        color: "#6b7280",
                        cursor: "pointer",
                        alignSelf: "flex-start",
                        textDecoration: "underline",
                      }}
                    >
                      Verwijderen
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          </div>
        </div>
      )}

      {/* In-app strategy summary view (legacy overlay, only when not using unified layout). */}
      {!USE_UNIFIED_TOP_BAR && mainView === "strategySummary" && loadedStrategy && (() => {
        const c = summaryCalc;
        const totalTermYears = c.effectivePlanYears + (c.enableDelay ? c.delayYears : 0);
        const totalTermMonths = totalTermYears * 12;
        const finalBalance = c.schedule?.length ? (c.schedule[c.schedule.length - 1]?.balance ?? 0) : 0;
        const isNotPaidOffWithinTerm = c.payoffMonths >= totalTermMonths && finalBalance > 0 && !c.payoffByInvestment;
        const gr = c.growthRate / 1000;
        const igr = c.enableSeparateGrowth ? c.investmentGrowthRate / 1000 : gr;
        const delayMonthsTotal = c.enableDelay ? c.delayYears * 12 + c.delayMonths : 0;
        const maxYearsForData = c.enableDelay && (c.delayYears > 0 || c.delayMonths > 0) ? totalTermYears : c.effectivePlanYears;
        const oneTimeReps = c.oneTimeRepayments || [];
        const summaryYearlyTableData = [];
        const payoffYearCap = Math.max(0, Math.floor((c.payoffMonths - 1) / 12)); // Laatste jaar-index (0-based)
        for (let y = 0; y <= maxYearsForData; y++) {
          if (y > payoffYearCap) break;
          const yearEndMonth = Math.min((y + 1) * 12, c.payoffMonths);
          const s = sampleAtMonth(c.schedule, yearEndMonth);
          let balanceAtEnd = Math.max(0, s.balance ?? 0);
          const invValue = s.investmentValue ?? 0;
          if (c.enableInvestment && invValue > balanceAtEnd && balanceAtEnd > 0.01) {
            balanceAtEnd = 0;
          }
          if (y === payoffYearCap) balanceAtEnd = 0; // Laatste rij = aflossingsjaar, altijd 0 schuld
          const yearsElapsed = Math.max(0, y - delayMonthsTotal / 12);
          let monthlyPay = 0;
          let monthlyInv = 0;
          if ((y + 1) * 12 > delayMonthsTotal) {
            monthlyPay = c.constrainedMonthlyPayment * (c.enableGrowth ? Math.pow(1 + gr, Math.floor(yearsElapsed)) : 1);
            monthlyInv = c.enableInvestment ? c.monthlyInvestment * (c.enableSeparateGrowth ? Math.pow(1 + igr, Math.floor(yearsElapsed)) : (c.enableGrowth ? Math.pow(1 + gr, Math.floor(yearsElapsed)) : 1)) : 0;
          }
          const oneTimeThisYear = oneTimeReps
            .filter((r) => r && (r.amount || 0) > 0)
            .filter((r) => r.year === 0 || (r.year || 0) === y + 1)
            .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          summaryYearlyTableData.push({ year: y + 1, monthlyPayment: Math.round(monthlyPay), monthlyInvestment: Math.round(monthlyInv), oneTimeThisYear: Math.round(oneTimeThisYear), investmentValue: Math.round(s.investmentValue ?? 0), interestPaid: Math.round(s.interestCumulative ?? 0), balance: Math.round(balanceAtEnd) });
          if (balanceAtEnd <= 0.01) break;
          if (c.enableInvestment && invValue > (s.balance ?? 0) && (s.balance ?? 0) > 0.01) break;
          if (yearEndMonth >= c.payoffMonths) break;
        }
        const summaryContent = {
          payment: Math.round(c.constrainedMonthlyPayment),
          debt: Math.round(c.totalDebt),
          growthPct: (c.growthRate / 10).toFixed(1),
          investmentGrowthPct: (c.investmentGrowthRate / 10).toFixed(1),
          delayYears: c.delayYears,
          delayMonths: c.delayMonths,
          investment: Math.round(c.monthlyInvestment),
          totalTermYears,
          payoffYears: c.payoffYears,
          payoffRemainderMonths: c.payoffRemainderMonths,
          interestTotal: Math.round(c.interestAtEnd ?? 0),
          finalInv: Math.round(c.finalInvestmentValue ?? 0),
          hasGrowth: c.enableGrowth && c.growthRate > 0,
          hasInvestmentGrowth: c.enableSeparateGrowth && c.investmentGrowthRate > 0,
          hasDelay: c.enableDelay && (c.delayYears > 0 || c.delayMonths > 0),
          hasInvestment: c.enableInvestment && c.monthlyInvestment > 0,
          isNotPaidOff: isNotPaidOffWithinTerm,
        };
        const fmtVal = (val, fmt) => {
          if (val == null || (typeof val === "number" && isNaN(val))) return "—";
          if (fmt === "eur") return `€ ${Math.round(val).toLocaleString("nl-NL")}`;
          return String(val);
        };
        const TILE_IDS = [
          { id: "debt", label: "Studieschuld", valueKey: "totalDebt", fmt: "eur" },
          { id: "monthly", label: "Maandelijkse aflossing", valueKey: "constrainedMonthlyPayment", fmt: "eur" },
          { id: "years", label: "Jaar tot aflossing", valueKey: "payoffYears", fmt: null },
          { id: "totalCost", label: "Totale kosten", valueKey: "totalCostWithInvestments", fmt: "eur" },
          { id: "interest", label: "Rente betaald", valueKey: "interestAtEnd", fmt: "eur" },
          { id: "investment", label: "Waarde investeringen op einde looptijd", valueKey: "finalInvestmentValue", fmt: "eur" },
        ];
        const tileVals = { totalDebt: c.totalDebt, constrainedMonthlyPayment: c.constrainedMonthlyPayment, payoffYears: c.payoffYears, totalCostWithInvestments: c.totalCostWithInvestments, interestAtEnd: c.interestAtEnd, finalInvestmentValue: c.finalInvestmentValue };
        return (
          <div style={{
            position: "absolute",
            ...(USE_UNIFIED_TOP_BAR ? { top: TOP_BAR_HEIGHT, left: 0, right: 0, bottom: 0 } : { inset: 0 }),
            display: "flex",
            flexDirection: "column",
            background: bg,
            zIndex: 1
          }}>
            {!USE_UNIFIED_TOP_BAR && (
            <header style={{
              flexShrink: 0,
              height: TOP_BAR_HEIGHT,
              background: "#fff",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 24px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
            }}>
              <a href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
                <img src="/logo-name.svg" alt="Slimmer af" style={{ height: 36, width: "auto", display: "block" }} />
              </a>
              <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                <button type="button" onClick={openSaveStrategyModal} disabled={isSavingStrategy}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 9999,
                    border: "none",
                    ...isSavingStrategy ? { background: "#e5e7eb" } : PORTAL_GRADIENT,
                    color: isSavingStrategy ? "#9ca3af" : "#FFFFFF",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: isSavingStrategy ? "not-allowed" : "pointer",
                    boxShadow: isSavingStrategy ? "none" : "0 2px 8px rgba(51,153,255,0.3)",
                    transition: "all 0.2s ease"
                  }}
                >
                  {isSavingStrategy ? "Bezig met opslaan…" : "Sla strategie op"}
                </button>
                <div style={{ width: 1, height: 24, background: "#e5e7eb", margin: "0 20px", flexShrink: 0 }} aria-hidden />
                <nav style={{ display: "flex", alignItems: "center", gap: "clamp(12px, 2vw, 24px)" }}>
                  <button type="button" onClick={() => setMainView("portal")}
                    style={{ padding: "8px 0", borderRadius: 0, border: "none", background: "transparent", color: "#4b5563", fontSize: 15, fontWeight: 500, cursor: "pointer", textDecoration: "none", transition: "all 0.2s ease" }}
                  >
                    Strategie samenstellen
                  </button>
                  <button type="button" onClick={() => setMainView("saved")}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 9999,
                      border: "none",
                      ...PORTAL_GRADIENT,
                      color: "#fff",
                      fontSize: 15,
                      fontWeight: 600,
                      boxShadow: "0 2px 8px rgba(51,153,255,0.3)"
                    }}
                  >
                    Opgeslagen strategieën
                  </button>
                  <a href="/#faq" style={{ padding: "8px 0", textDecoration: "none", background: "transparent", color: "#4b5563", fontSize: 15, fontWeight: 500, transition: "all 0.2s ease" }}>
                    FAQ
                  </a>
                </nav>
              </div>
            </header>
            )}
            <div style={{ flex: 1, overflowY: "auto" }}>
            <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
              <button type="button" onClick={() => setMainView("saved")} style={{ marginBottom: 24, background: "none", border: "1px solid #d1d5db", borderRadius: 10, fontSize: 14, color: "#374151", padding: "10px 18px", cursor: "pointer" }}>
                ← Terug naar opgeslagen strategieën
              </button>
              <h1 style={{ margin: "0 0 4px 0", fontSize: 28, fontWeight: 700, color: "#111827" }}>Samenvatting Strategie</h1>
              <p style={{ fontSize: 15, color: "#6b7280", marginBottom: 24 }}>{loadedStrategy.name}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                {TILE_IDS.map((t) => {
                  const val = tileVals[t.valueKey];
                  const isYearsTile = t.valueKey === "payoffYears";
                  const isInvestmentTile = t.valueKey === "finalInvestmentValue";
                  const yearsDisplay = isYearsTile && isNotPaidOffWithinTerm ? "Niet binnen termijn" : isYearsTile ? (c.payoffRemainderMonths > 0 ? `${c.payoffYears} jaar ${c.payoffRemainderMonths} mnd` : `${c.payoffYears} jaar`) : null;
                  const valueDisplay = isYearsTile ? yearsDisplay : isInvestmentTile && (val == null || !c.enableInvestment) ? "—" : fmtVal(val, t.fmt);
                  return (
                    <div key={t.id} style={{ ...PORTAL_GRADIENT, borderRadius: 16, padding: 20, color: "#fff", boxShadow: `0 4px 12px ${PORTAL_ACCENT_RGBA(0.25)}` }}>
                      <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 4 }}>{t.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 700 }}>{valueDisplay}</div>
                      {t.valueKey === "constrainedMonthlyPayment" && c.enableGrowth && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>+{(c.growthRate / 10).toFixed(1)}%/jr stijging</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: 20, background: "#f9fafb", borderRadius: 16, fontSize: 15, lineHeight: 1.7, color: "#374151", marginBottom: 24 }}>
                <p style={{ margin: "0 0 12px 0" }}><strong>Wat is de strategie?</strong> Je lost <strong>€{summaryContent.payment.toLocaleString("nl-NL")} per maand</strong> af op je studieschuld van <strong>€{summaryContent.debt.toLocaleString("nl-NL")}</strong>.
                  {summaryContent.hasGrowth && <> Je verhoogt je aflossing <strong>jaarlijks met {summaryContent.growthPct}%</strong>.</>}
                  {summaryContent.hasDelay && <> Je stelt de aflossing <strong>{summaryContent.delayYears > 0 && summaryContent.delayMonths > 0 ? `${summaryContent.delayYears} jaar en ${summaryContent.delayMonths} maanden` : summaryContent.delayYears > 0 ? `${summaryContent.delayYears} jaar` : `${summaryContent.delayMonths} maanden`} uit</strong>.</>}
                  {summaryContent.hasInvestment && <> Daarnaast investeer je <strong>€{summaryContent.investment.toLocaleString("nl-NL")} per maand</strong>.{summaryContent.hasInvestmentGrowth && <> Ook je inleg laat je <strong>jaarlijks met {summaryContent.investmentGrowthPct}%</strong> stijgen.</>}</>}
                </p>
                <p style={{ margin: 0 }}><strong>Wat is de uitkomst?</strong>
                  {summaryContent.isNotPaidOff ? <> Binnen de looptijd van <strong>{summaryContent.totalTermYears} jaar</strong> wordt de lening <strong>niet</strong> afgelost.</> : <> De lening is afgelost in <strong>{summaryContent.payoffRemainderMonths > 0 ? `${summaryContent.payoffYears} jaar en ${summaryContent.payoffRemainderMonths} maanden` : `${summaryContent.payoffYears} jaar`}</strong>. Je betaalt in totaal <strong>€{summaryContent.interestTotal.toLocaleString("nl-NL")}</strong> aan rente.{summaryContent.hasInvestment && summaryContent.finalInv > 0 && <> De waarde van je investeringen aan het eind bedraagt <strong>€{summaryContent.finalInv.toLocaleString("nl-NL")}</strong>.</>}</>}
                </p>
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1f2937", marginBottom: 12 }}>Aflosschema</h2>
              <div style={{ overflowX: "auto", marginBottom: 12 }}>
                <table style={{ width: "100%", minWidth: 600, borderCollapse: "collapse", fontSize: 14, tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "8%" }}>Jaar</th>
                      <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "18%" }}>Maandelijkse aflossing</th>
                      <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "18%" }}>Maandelijkse investering</th>
                      <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "16%" }}>Eenmalige aflossingen</th>
                      <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "16%" }}>Waarde investeringen (eind)</th>
                      <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "14%" }}>Totaal betaalde rente</th>
                      <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "14%" }}>Restant schuld</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryYearlyTableData.map((row) => (
                      <tr key={row.year} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px", color: "#1f2937", fontWeight: 500 }}>{row.year}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.monthlyPayment ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.monthlyInvestment ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.oneTimeThisYear ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.investmentValue ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.interestPaid ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                        <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.balance ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}><strong>Betaalschema</strong>: laat het verloop van je maandelijkse betaling zien (incl. jaarlijkse stijging als je die inschakelt). De lijn stopt zodra de lening is afbetaald.</p>
            </div>
            </div>
          </div>
        );
      })()}

      {/* Investment Explanation Modal */}
      {showInvestmentExplanation && (
        <>
          {/* Blur overlay - shows portal container contours */}
          <div style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(8px)",
            zIndex: 10001,
            pointerEvents: "none"
          }} />
          {/* Modal */}
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10002,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}>
            <div style={{
              background: "#FFFFFF",
              borderRadius: 24,
              padding: "40px",
              maxWidth: 800,
              width: "100%",
              maxHeight: "90vh",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              position: "relative",
              display: "flex",
              flexDirection: "column"
            }}>
              {/* Close button */}
              <button
                onClick={() => setShowInvestmentExplanation(false)}
                style={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  background: "transparent",
                  border: "none",
                  fontSize: 24,
                  color: "#6B7280",
                  cursor: "pointer",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#F3F4F6";
                  e.target.style.color = "#111827";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.color = "#6B7280";
                }}
              >
                ×
              </button>
              
              {/* Content - scrollable */}
              <div style={{
                overflowY: "auto",
                paddingRight: 20,
                flex: 1
              }}>
                <h2 style={{
                  marginTop: 0,
                  marginBottom: 24,
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#111827"
                }}>
                  Waarom zou je investeren in plaats van alleen extra aflossen?
                </h2>
                
                <p style={{
                  marginBottom: 24,
                  fontSize: 16,
                  color: "#374151",
                  lineHeight: 1.6
                }}>
                  Veel mensen vragen zich af wat slimmer is: meer aflossen op je studieschuld of een deel van je geld investeren. Hieronder leggen we stap voor stap uit waarom investeren in sommige situaties voordeliger kan zijn, zelfs als je doel uiteindelijk is om je studieschuld eerder af te lossen.
                </p>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    Investeren: de basics
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Investeren betekent dat je je geld gebruikt om iets te kopen dat in waarde kan groeien — zoals aandelen (je bezit dan een klein stukje van een bedrijf) of een indexfonds (een mix van veel verschillende bedrijven).<br />
                    Het verschil met sparen? Op je spaarrekening krijg je bijna geen rente meer. Door te investeren kan je geld harder groeien. Natuurlijk kan de waarde ook dalen (risico!), maar op de lange termijn (10+ jaar) heeft de markt historisch gezien gemiddeld 6-8% per jaar opgeleverd. Dat is veel meer dan de 2,21% of 2,57% rente op je studieschuld. Dus als je het niet erg vindt om te wachten, kan investeren je flink helpen.
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    1. Wat is het voordeel van investeren ten opzichte van aflossen?
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    De rente op je studieschuld is laag (2,57%). Dat betekent dat het relatief goedkoop is om die schuld rustig in de standaardtermijn af te lossen.<br />
                    Als je investeert, kun je op de lange termijn vaak een hoger gemiddeld rendement halen dan die 2,57%. Daardoor groeit je geld sneller dan het bedrag dat je zou besparen door extra af te lossen.
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    2. Wat is "rente-op-rente" en waarom is het zo krachtig?
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Bij investeren werkt je rendement als een soort sneeuwbal: Je behaalt rendement op je inleg. Maar daarna levert dat rendement óók weer rendement op. Dat heet rente-op-rente (compound interest), ook wel het 8e wereldwonder genoemd door Einstein, en het zorgt ervoor dat je vermogen steeds harder gaat groeien — vooral als je vroeg begint en langere tijd laat staan.
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    3. Hoe helpt investeren mij om eerder van mijn studieschuld af te zijn?
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Doordat je vermogen groeit, kun je later met één groter bedrag een flinke extra aflossing doen — soms zelfs genoeg om de schuld in één keer weg te werken.<br />
                    In plaats van elke maand extra aflossen, laat je je geld eerst groeien en gebruik je het vervolgens strategisch om sneller schuldenvrij te worden.
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    4. Is dat niet veel risicovoller?
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Beleggen brengt altijd risico's met zich mee. De waarde kan schommelen, en je kunt (een deel van) je inleg verliezen.<br />
                    Daarom is investeren vooral interessant voor doelen op de langere termijn. Hoe langer je horizon, hoe meer tijd schommelingen hebben om uit te middelen.
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    5. Wat als ik liever zekerheid heb?
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Extra aflossen geeft direct en gegarandeerd resultaat: je schuld wordt lager en je betaalt minder rente.<br />
                    Investeren kiest voor potentieel hogere groei — maar met onzekerheid. Het hangt dus af van jouw voorkeur: zekerheid nu of mogelijk meer voordeel later.
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    6. Hoe kun je investeren?
                  </h3>
                  <p style={{
                    margin: 0,
                    marginBottom: 12,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Je kunt beginnen met investeren via grote, betrouwbare aanbieders zoals banken (bijvoorbeeld ABN AMRO, ING, Rabobank) of online brokers (zoals DEGIRO, eToro, of Trading 212). Deze aanbieders bieden eenvoudige platforms waar je met lage kosten kunt beleggen.
                  </p>
                  <p style={{
                    margin: 0,
                    marginBottom: 12,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    <strong>Hoe begin je?</strong> Open een beleggingsrekening bij een aanbieder naar keuze. Dit kan meestal online binnen een paar minuten. Vervolgens kun je al starten met kleine bedragen (soms vanaf €25 of €50) en maandelijks automatisch geld inleggen. Je stelt eenvoudig in dat er elke maand automatisch een bedrag van je bankrekening wordt afgeschreven en belegd.
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    <strong>Wat beleg je in?</strong> Voor beginners zijn brede indexfondsen of ETF's (Exchange Traded Funds) een goede keuze. Deze volgen automatisch een hele index, zoals de AEX of S&P 500. Hierdoor ben je automatisch gespreid over honderden of duizenden bedrijven, wat het risico verlaagt. Je hoeft niet zelf te kiezen welke bedrijven je koopt - het fonds doet dat voor je.
                  </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 12,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    7. Welk rendement gebruiken jullie en waarom?
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Wij gebruiken een verwacht rendement van 6% per jaar. Het historische gemiddelde rendement van brede aandelenmarkten over lange periodes ligt normaliter tussen de 6-8% per jaar. Wij kiezen voor 6% als redelijk conservatieve schatting, ook om mogelijke transactiekosten van aanbieders mee te nemen. Het is belangrijk om te weten dat dit een gemiddelde is: in sommige jaren kan het rendement hoger zijn, in andere jaren lager of zelfs negatief. Beleggen blijft altijd risicovol en het verleden is geen garantie voor de toekomst.
                  </p>
                </div>

                <div style={{
                  marginTop: 32,
                  padding: 28,
                  background: "#F9FAFB",
                  borderRadius: 12,
                  border: "1px solid #E5E7EB"
                }}>
                  <h3 style={{
                    marginTop: 0,
                    marginBottom: 16,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827"
                  }}>
                    Kort samengevat
                  </h3>
                  <p style={{
                    margin: 0,
                    marginBottom: 12,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Investeren kan interessant zijn omdat:
                  </p>
                  <ul style={{
                    margin: 0,
                    paddingLeft: 20,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.8
                  }}>
                    <li>de rente op je studieschuld laag is (2,57%),</li>
                    <li>investeren op lange termijn vaak meer oplevert,</li>
                    <li>rente-op-rente je vermogen steeds sneller laat groeien,</li>
                    <li>je dat vermogen later kunt gebruiken om je schuld juist eerder af te lossen.</li>
                  </ul>
                  <p style={{
                    margin: 0,
                    marginTop: 16,
                    fontSize: 16,
                    color: "#374151",
                    lineHeight: 1.6
                  }}>
                    Maar: beleggen brengt risico's met zich mee, dus het is belangrijk dat je je hier prettig bij voelt.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* PC module: one layout for portal, opgeslagen strategieën, and strategy summary. Same top bar always; body content switches. */}
      {(mainView === "portal" || mainView === "saved" || mainView === "strategySummary" || mainView === "faq") && (
      <>
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        zIndex: 1,
        overflow: "hidden",
        fontFamily: "'Poppins', sans-serif"
      }}>
        <style>{BOX_SLIDER_GLOBAL_STYLE}</style>
        {/* Custom radio: blue fill only inside ring, no bleed */}
        <style>{`
          .portal-radio-custom {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border: 2px solid #374151;
            border-radius: 50%;
            background: #fff;
            box-sizing: border-box;
            flex-shrink: 0;
            margin: 0;
            cursor: pointer;
          }
          .portal-radio-custom:checked {
            border-color: #374151;
            background: #fff;
            box-shadow: inset 0 0 0 5px #3399FF;
          }
        `}</style>
        {/* Top bar: logo + nav (current screen highlighted in blue) */}
        <header style={{
          flexShrink: 0,
          height: TOP_BAR_HEIGHT,
          background: "#fff",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 24px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
        }}>
          <a href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-name.svg" alt="Slimmer af" style={{ height: 36, width: "auto", display: "block" }} />
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <button
              type="button"
              onClick={openSaveStrategyModal}
              disabled={isSavingStrategy}
              style={{
                padding: "10px 20px",
                borderRadius: 9999,
                border: "none",
                ...isSavingStrategy ? { background: "#e5e7eb" } : PORTAL_GRADIENT,
                color: isSavingStrategy ? "#9ca3af" : "#FFFFFF",
                fontSize: 15,
                fontWeight: 600,
                cursor: isSavingStrategy ? "not-allowed" : "pointer",
                boxShadow: isSavingStrategy ? "none" : "0 2px 8px rgba(51,153,255,0.3)",
                transition: "all 0.2s ease"
              }}
            >
              {isSavingStrategy ? "Bezig met opslaan…" : "Sla strategie op"}
            </button>
            <div style={{ width: 1, height: 24, background: "#e5e7eb", margin: "0 20px", flexShrink: 0 }} aria-hidden />
            <nav style={{ display: "flex", alignItems: "center", gap: "clamp(12px, 2vw, 24px)" }}>
              <button
                type="button"
                onClick={() => setMainView("portal")}
                style={{
                  padding: mainView === "portal" ? "10px 20px" : "8px 0",
                  borderRadius: mainView === "portal" ? 9999 : 0,
                  border: "none",
                  ...(mainView === "portal" ? PORTAL_GRADIENT : { background: "transparent" }),
                  color: mainView === "portal" ? "#fff" : "#4b5563",
                  fontSize: 15,
                  fontWeight: mainView === "portal" ? 600 : 500,
                  cursor: "pointer",
                  boxShadow: mainView === "portal" ? "0 2px 8px rgba(51,153,255,0.3)" : "none",
                  textDecoration: "none",
                  transition: "all 0.2s ease"
                }}
              >
                Strategie samenstellen
              </button>
              <button
                type="button"
                onClick={() => setMainView("saved")}
                style={{
                  padding: (mainView === "saved" || mainView === "strategySummary") ? "10px 20px" : "8px 0",
                  borderRadius: (mainView === "saved" || mainView === "strategySummary") ? 9999 : 0,
                  border: "none",
                  ...((mainView === "saved" || mainView === "strategySummary") ? PORTAL_GRADIENT : { background: "transparent" }),
                  color: (mainView === "saved" || mainView === "strategySummary") ? "#fff" : "#4b5563",
                  fontSize: 15,
                  fontWeight: (mainView === "saved" || mainView === "strategySummary") ? 600 : 500,
                  cursor: "pointer",
                  boxShadow: (mainView === "saved" || mainView === "strategySummary") ? "0 2px 8px rgba(51,153,255,0.3)" : "none",
                  textDecoration: "none",
                  transition: "all 0.2s ease"
                }}
              >
                Opgeslagen strategieën
              </button>
              <button
                type="button"
                onClick={() => setMainView("faq")}
                style={{
                  padding: mainView === "faq" ? "10px 20px" : "8px 0",
                  borderRadius: mainView === "faq" ? 9999 : 0,
                  border: "none",
                  ...(mainView === "faq" ? PORTAL_GRADIENT : { background: "transparent" }),
                  color: mainView === "faq" ? "#fff" : "#4b5563",
                  fontSize: 15,
                  fontWeight: mainView === "faq" ? 600 : 500,
                  cursor: "pointer",
                  boxShadow: mainView === "faq" ? "0 2px 8px rgba(51,153,255,0.3)" : "none",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  fontFamily: "inherit"
                }}
              >
                FAQ
              </button>
              {SHOW_TUTORIAL ? (
                <button
                  type="button"
                  onClick={handleRestartTutorial}
                  aria-label="Restart tutorial"
                  style={{
                    padding: "8px 0",
                    borderRadius: 0,
                    border: "none",
                    background: "transparent",
                    color: "#4b5563",
                    fontSize: 15,
                    fontWeight: 500,
                    cursor: "pointer",
                    boxShadow: "none",
                    textDecoration: "none",
                    transition: "all 0.2s ease",
                    fontFamily: "inherit",
                  }}
                >
                  Restart tutorial
                </button>
              ) : null}
            </nav>
          </div>
        </header>

        {/* Body: sidebar + right area (grey background) */}
        <div style={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          position: "relative",
          background: bg
        }}>
          {/* Blur overlays for guide (positioned over sidebar / main) */}
          {/* Resultaat-stappen: viewport-blur + infokaart via createPortal onderaan */}

          {/* Step 1: Right side blur + explanation (explains left side) */}
          {shouldBlurRight && (
            <>
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
                  background: "rgba(255,255,255,0.4)",
                  backdropFilter: "blur(8px)",
                  zIndex: 100,
                  pointerEvents: "auto",
                }}
              />
              <div style={{
                position: "absolute",
                top: "50%",
                left: (sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED) + 320,
                transform: "translate(-50%, -50%)",
                background: "#FFFFFF",
                borderRadius: 16,
                padding: "24px 28px",
                maxWidth: 420,
                width: "85%",
                minHeight: 380,
                boxSizing: "border-box",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                zIndex: 101,
                border: "2px solid #3399FF",
                display: "flex",
                flexDirection: "column"
              }}>
                <div style={{
                  position: "absolute",
                  right: "100%",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 0,
                  height: 0,
                  borderTop: "20px solid transparent",
                  borderBottom: "20px solid transparent",
                  borderRight: "28px solid #3399FF"
                }} />
                {guideSidebarInputCard ? (
                  <>
                    <h3 style={{ margin: 0, marginBottom: 10, fontSize: 20, fontWeight: 700, color: "#111827" }}>{guideSidebarInputCard.title}</h3>
                    <div style={{ margin: 0, marginBottom: 16, fontSize: 15, color: "#4B5563", lineHeight: 1.6, flex: 1 }}>
                      <p style={{ margin: 0 }}>{guideSidebarInputCard.body}</p>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "stretch", marginTop: "auto" }}>
                      <button
                        type="button"
                        onClick={goGuideInputPrevious}
                        style={{
                          padding: "12px 16px",
                          borderRadius: 10,
                          border: "2px solid #d1d5db",
                          background: "#fff",
                          color: "#374151",
                          fontWeight: 600,
                          fontSize: 15,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Terug
                      </button>
                      <button
                        type="button"
                        onClick={goGuideInputNext}
                        style={{
                          padding: "12px 20px",
                          borderRadius: 10,
                          border: "none",
                          ...PORTAL_GRADIENT,
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: 18,
                          flex: 1,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Volgende
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </>
          )}

          {/* Left sidebar: only on portal view. Never on opgeslagen strategieën or strategy summary (always use top bar only). */}
          {mainView === "portal" && (
          <aside style={{
            flexShrink: 0,
            width: sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
            height: "100%",
            background: "#fff",
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
            transition: "width 0.25s ease",
            boxShadow: sidebarExpanded ? "4px 0 24px rgba(0,0,0,0.08)" : "none",
            borderRight: "1px solid #e5e7eb"
          }}>
            {/* Icon column: always visible, fixed left, same width */}
            <div style={{
              flexShrink: 0,
              width: SIDEBAR_WIDTH_COLLAPSED,
              height: "100%",
              padding: "12px 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              borderRight: "1px solid #e5e7eb"
            }}>
              <button
                type="button"
                onClick={() => setSidebarExpanded((prev) => !prev)}
                aria-label={sidebarExpanded ? "Menu verbergen" : "Menu tonen"}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  border: "none",
                  background: "#1f2937",
                  color: "#d1d5db",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <div style={{
                width: "100%",
                marginTop: 4,
                paddingTop: 12,
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8
              }}>
              {SIDEBAR_SECTIONS.map((sec) => (
                <React.Fragment key={sec.id}>
                  {sec.id === 'feedback' && (
                    <div style={{ width: "100%", marginTop: 2, paddingTop: 6, borderTop: "1px solid #e5e7eb" }} />
                  )}
                <div
                  style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}
                  onMouseEnter={(e) => { setSidebarHoverLabel(sec.id); setSidebarHoverPos({ x: e.clientX, y: e.clientY }); }}
                  onMouseMove={(e) => setSidebarHoverPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setSidebarHoverLabel(null)}
                >
                <button
                  type="button"
                  onClick={() => onSidebarIconClickForTutorial(sec.id)}
                  aria-label={sec.label}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    border: "none",
                    background: sidebarSection === sec.id ? "#3399FF" : "#f3f4f6",
                    color: sidebarSection === sec.id ? "#e5e7eb" : "#374151",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0
                  }}
                >
                  {sec.id === 'info' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sidebarSection === 'info' ? "#e5e7eb" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  )}
                  {sec.id === 'gegevens' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sidebarSection === 'gegevens' ? "#e5e7eb" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  )}
                  {sec.id === 'aflosstrategie' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sidebarSection === 'aflosstrategie' ? "#e5e7eb" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="4" y1="21" x2="4" y2="14" />
                      <line x1="4" y1="10" x2="4" y2="3" />
                      <line x1="12" y1="21" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12" y2="3" />
                      <line x1="20" y1="21" x2="20" y2="16" />
                      <line x1="20" y1="12" x2="20" y2="3" />
                      <line x1="1" y1="14" x2="7" y2="14" />
                      <line x1="9" y1="8" x2="15" y2="8" />
                      <line x1="17" y1="16" x2="23" y2="16" />
                    </svg>
                  )}
                  {sec.id === 'delay' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sidebarSection === 'delay' ? "#e5e7eb" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  )}
                  {sec.id === 'investeren' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sidebarSection === 'investeren' ? "#e5e7eb" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="20" x2="12" y2="10" />
                      <line x1="18" y1="20" x2="18" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="16" />
                    </svg>
                  )}
                  {sec.id === 'eenmalig' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sidebarSection === 'eenmalig' ? "#e5e7eb" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="16" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                    </svg>
                  )}
                  {sec.id === 'feedback' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sidebarSection === 'feedback' ? "#e5e7eb" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  )}
                </button>
                {sidebarHoverLabel === sec.id && (
                  <div style={{
                    position: "fixed",
                    left: sidebarHoverPos.x + 12,
                    top: sidebarHoverPos.y - 16,
                    padding: "6px 12px",
                    borderRadius: 9999,
                    background: "#3399FF",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 9999,
                    pointerEvents: "none"
                  }}>
                    {sec.label}
                  </div>
                )}
                </div>
                </React.Fragment>
              ))}
              </div>
            </div>
            {sidebarExpanded && (
            <div style={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              padding: "0 20px 20px 20px",
              paddingTop: sidebarSection === 'feedback' ? 20 : (sidebarSection !== null ? 44 : undefined),
              display: "flex",
              flexDirection: "column"
            }}>
              {sidebarSection === 'info' && (
                <div style={{ marginBottom: 24 }}>
                  <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, fontWeight: 700, color: "#111827" }}>Info</h2>
                  <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: "#374151" }}>
                    Met deze aflosplanner krijg je inzicht in je studieschuld: wat is je minimale maandelijkse aflossing, hoe lang doe je erover om alles terug te betalen, en wat zijn de gevolgen van je keuzes? Je kunt verschillende scenario’s met elkaar vergelijken: alleen aflossen, aflossen combineren met beleggen, eenmalige aflossingen inplannen of het begin van je aflossing uitstellen. Alle berekeningen passen zich direct aan op basis van jouw invoer, zodat je in één oogopslag ziet wat het effect is van hoger aflossen, beleggen of later starten.
                  </p>
                </div>
              )}

          {sidebarSection === 'gegevens' && (
          <>
          <h2 style={{ marginTop: 0, marginBottom: 20, color: "#111827" }}>Basisinformatie input</h2>
          {/* inputs omitted for brevity - unchanged below */}
          {/* 1. Totale schuld */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#374151" }}>Totale schuld</label>
            </div>
            <input type="number" min="0" step="0.01" value={totalDebt === 0 ? "" : totalDebt}
              onChange={(e) => {
                const val = e.target.value;
                setTotalDebt(val === "" ? 0 : Number(val));
              }}
              onFocus={() => setFocusedInput('totalDebt')}
              onBlur={() => setFocusedInput(null)}
              placeholder="Bijv. 23000"
              style={{
                ...(focusedInput === 'totalDebt' ? inputStyleFocusedWhite : inputStyleWhite),
                width: "100%",
                maxWidth: "none"
              }} />
          </div>
          {/* 2. Aflosstelsel */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <label style={{ marginRight: 6, fontSize: 14, fontWeight: 600, color: "#374151" }}>Aflosstelsel</label>
              <Info text="Je aflosstelsel bepaalt rente en looptijd: 15 jaar (nieuwe regeling) of 35 jaar (oude regeling). Welk stelsel voor jou geldt zie je in Mijn DUO bij ‘Mijn studieschuld’/‘Aflossen’. Ben je vóór 2023 begonnen met studeren? Dan val je meestal onder 35 jaar; anders vaak 15 jaar. Kies hier het stelsel dat in Mijn DUO staat." />
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 8, 
                padding: "12px 16px",
                borderRadius: 12,
                background: planYears === 15 ? "#e5e7eb" : "#f9fafb",
                border: planYears === 15 ? "2px solid #3399FF" : "2px solid #e5e7eb",
                cursor: "pointer",
                transition: "all 0.3s ease",
                fontSize: 14,
                fontWeight: 500
              }}>
                <input type="radio" name="stelsel" value="15" checked={planYears === 15} onChange={(e) => setRepaymentPlan(e.target.value)} className="portal-radio-custom" />
                15 jaar
              </label>
              <label style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 8, 
                padding: "12px 16px",
                borderRadius: 12,
                background: planYears === 35 ? "#e5e7eb" : "#f9fafb",
                border: planYears === 35 ? "2px solid #3399FF" : "2px solid #e5e7eb",
                cursor: "pointer",
                transition: "all 0.3s ease",
                fontSize: 14,
                fontWeight: 500
              }}>
                <input type="radio" name="stelsel" value="35" checked={planYears === 35} onChange={(e) => setRepaymentPlan(e.target.value)} className="portal-radio-custom" />
                35 jaar
              </label>
            </div>
            <div style={{ marginTop: 20, marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Al begonnen met aflossen?</label>
                <Info text="Heb je al eerder aflossingen gedaan? Als je jaarlijks aflost, vul hier in hoeveel jaar je al bezig bent. De resterende termijn wordt dan berekend (bijv. 35 jaar stelsel - 2 jaar = 33 jaar resterend)." />
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                <label style={{ 
                  display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 12,
                background: !hasStartedRepaying ? "#e5e7eb" : "#f9fafb",
                border: !hasStartedRepaying ? "2px solid #3399FF" : "2px solid #e5e7eb",
                  cursor: "pointer", fontSize: 14, fontWeight: 500
                }}>
                  <input type="radio" name="startedRepaying" checked={!hasStartedRepaying} onChange={() => setHasStartedRepaying(false)} className="portal-radio-custom" />
                  Nee
                </label>
                <label style={{ 
                  display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 12,
                background: hasStartedRepaying ? "#e5e7eb" : "#f9fafb",
                border: hasStartedRepaying ? "2px solid #3399FF" : "2px solid #e5e7eb",
                  cursor: "pointer", fontSize: 14, fontWeight: 500
                }}>
                  <input type="radio" name="startedRepaying" checked={hasStartedRepaying} onChange={() => setHasStartedRepaying(true)} className="portal-radio-custom" />
                  Ja
                </label>
              </div>
              {hasStartedRepaying && (
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                    Hoeveel jaar ben je al bezig met aflossen?
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={Math.max(0, planYears - 1)}
                    step={1}
                    value={yearsAlreadyRepaid === 0 ? "" : yearsAlreadyRepaid}
                    onChange={(e) => {
                      const v = e.target.value;
                      const n = v === "" ? 0 : Math.min(Math.max(0, Number(v)), planYears - 1);
                      setYearsAlreadyRepaid(n);
                    }}
                    onFocus={() => setFocusedInput('yearsAlreadyRepaid')}
                    onBlur={() => setFocusedInput(null)}
                    placeholder={`0 t/m ${planYears - 1}`}
                    style={{ 
                      ...(focusedInput === 'yearsAlreadyRepaid' ? inputStyleFocusedWhite : inputStyleWhite),
                      width: "100%",
                      maxWidth: "none"
                    }}
                  />
                  <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
                    Resterende termijn: {effectivePlanYears} jaar
                  </div>
                </div>
              )}
            </div>
            {/* Zelf rente instellen */}
            <div style={{ marginTop: 24, marginBottom: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Zelf rente instellen</label>
                <Info text="Standaard wordt de DUO-rente van je aflosstelsel gebruikt (2,21% voor 15 jaar, 2,57% voor 35 jaar). Voor sommige situaties kan het percentage net afwijken; dat kun je hier invullen. Met deze optie kun je ook per periode een ander rentepercentage invoeren, bijvoorbeeld als je verwacht dat de rente wijzigt." />
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                <label style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 12,
                  background: !enableCustomRates ? "#e5e7eb" : "#f9fafb",
                  border: !enableCustomRates ? "2px solid #3399FF" : "2px solid #e5e7eb",
                  cursor: "pointer", fontSize: 14, fontWeight: 500
                }}>
                  <input type="radio" name="customRates" checked={!enableCustomRates} onChange={() => setEnableCustomRates(false)} className="portal-radio-custom" />
                  Nee
                </label>
                <label style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 12,
                  background: enableCustomRates ? "#e5e7eb" : "#f9fafb",
                  border: enableCustomRates ? "2px solid #3399FF" : "2px solid #e5e7eb",
                  cursor: "pointer", fontSize: 14, fontWeight: 500
                }}>
                  <input type="radio" name="customRates" checked={enableCustomRates} onChange={() => { setEnableCustomRates(true); if (!customRates?.length) setCustomRates([{ year: 0, ratePct: 0 }]); }} className="portal-radio-custom" />
                  Ja
                </label>
              </div>
              {!enableCustomRates && (
                <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>
                  Er wordt gebruikgemaakt van het rentepercentage van je aflosstelsel: {planYears === 15 ? "2,21%" : "2,57%"} ({planYears} jaar).
                </p>
              )}
              {enableCustomRates && (
                <>
                  {(() => {
                    const segments = [...(customRates || [])].filter((r) => r != null && typeof r.year === "number").sort((a, b) => a.year - b.year);
                    const usedYears = new Set(segments.map((r) => r.year));
                    const maxYear = Math.max(0, totalTermYears - 1);
                    return (
                      <>
                        {segments.length === 0 ? (
                          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
                            <span style={{ fontSize: 15, fontWeight: 500, color: "#374151", flex: 1, minWidth: 140 }}>Vanaf jaar 0</span>
                            <span style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
                              <input type="number" min={0} max={20} step={0.01} value={(customRates[0]?.ratePct ?? 0) === 0 ? "" : (customRates[0]?.ratePct ?? 0)} onChange={(e) => setCustomRates([{ year: 0, ratePct: e.target.value === "" ? 0 : Math.max(0, Math.min(20, Number(e.target.value))) }])} onFocus={() => setFocusedInput("rate0")} onBlur={() => setFocusedInput(null)} style={{ ...(focusedInput === "rate0" ? inputStyleFocusedWhite : inputStyleWhite), width: 80, flexShrink: 0 }} />
                              <span style={{ color: "#374151", fontSize: 14 }}>%</span>
                            </span>
                          </div>
                        ) : (
                          segments.map((seg, idx) => (
                            <div key={idx} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
                              {seg.year === 0 ? (
                                <span style={{ fontSize: 15, fontWeight: 500, color: "#374151", flex: 1, minWidth: 140 }}>Vanaf jaar 0</span>
                              ) : (
                                <select
                                  value={seg.year}
                                  onChange={(e) => {
                                    const newY = Math.max(1, Math.min(maxYear, Number(e.target.value)));
                                    const otherYears = new Set(segments.map((s, i) => (i === idx ? null : s.year)).filter(Boolean));
                                    if (otherYears.has(newY)) return;
                                    setCustomRates((prev) => prev.map((s, i) => (i === idx ? { ...s, year: newY } : s)).sort((a, b) => a.year - b.year));
                                  }}
                                  style={{ ...inputStyleWhite, flex: 1, minWidth: 140, maxWidth: 220 }}
                                >
                                  {Array.from({ length: maxYear + 1 }, (_, i) => i).filter((y) => y >= 1 && (y === seg.year || !usedYears.has(y))).map((y) => (
                                    <option key={y} value={y}>Vanaf jaar {y}</option>
                                  ))}
                                </select>
                              )}
                              <span style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
                              <input
                                type="number"
                                min={0}
                                max={20}
                                step={0.01}
                                value={seg.ratePct === 0 ? "" : seg.ratePct}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const pct = v === "" ? 0 : Math.max(0, Math.min(20, Number(v)));
                                  setCustomRates((prev) => prev.map((s, i) => (i === idx ? { ...s, ratePct: pct } : s)));
                                }}
                                onFocus={() => setFocusedInput("rate-" + idx)}
                                onBlur={() => setFocusedInput(null)}
                                style={{ ...(focusedInput === "rate-" + idx ? inputStyleFocusedWhite : inputStyleWhite), width: 80, flexShrink: 0 }}
                              />
                              <span style={{ color: "#374151", fontSize: 14 }}>%</span>
                            </span>
                            </div>
                          ))
                        )}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                          <button
                            type="button"
                            onClick={() => {
                              const nextYear = Array.from({ length: maxYear + 1 }, (_, i) => i).find((y) => !usedYears.has(y));
                              if (nextYear !== undefined) setCustomRates((prev) => [...prev, { year: nextYear, ratePct: 0 }].sort((a, b) => a.year - b.year));
                            }}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: 12, border: "1px solid #d1d5db", background: "#e5e7eb", color: "#374151", fontSize: 24, fontWeight: 400, cursor: "pointer", padding: 0, lineHeight: 1 }}
                            aria-label="Renteperiode toevoegen"
                            title="Renteperiode toevoegen"
                          >
                            +
                          </button>
                          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>Renteperiode toevoegen</span>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
            {/* 3. Minimale aflossing - dicht onder Zelf rente instellen */}
            <div style={{ marginTop: 12, marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Minimale aflossing DUO</label>
                <Info text="De minimale DUO-aflossing (maandbedrag) wordt berekend op basis van je draagkracht: je betaalt nooit meer dan 4% (stelsel 35 jaar) of 12% (stelsel 15 jaar) van je inkomen boven de draagkrachtvrije voet (€26.136 in 2026). Formule: (Bruto jaarloon − €26.136) × percentage / 12. DUO gebruikt het inkomen van 2 jaar geleden (peiljaar); bij lager inkomen kun je verlegging aanvragen. Je kunt 60 maanden aflosvrij aanvragen. Gebruik de rekenhulp op duo.nl voor een exacte berekening." />
              </div>
              {!showSalary && (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <input type="number" min="0" step="0.01" value={minPayment === 0 ? "" : minPayment}
                      onChange={(e) => {
                        const val = e.target.value;
                        setMinPayment(val === "" ? 0 : Number(val));
                      }}
                      onFocus={() => setFocusedInput('minPayment')}
                      onBlur={() => setFocusedInput(null)}
                      placeholder="Bijv. 120"
                      style={{
                        ...(focusedInput === 'minPayment' ? inputStyleFocusedWhite : inputStyleWhite),
                        width: "100%",
                        maxWidth: "none"
                      }} />
                    <button
                      type="button"
                      onClick={() => setShowSalary(true)}
                      style={{
                        flexShrink: 0,
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        background: "#f9fafb",
                        color: "#6b7280",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer"
                      }}
                    >
                      Weet ik niet
                    </button>
                  </div>
                </>
              )}
              {showSalary && (
                <div style={{ marginTop: 0 }}>
                  <button
                    type="button"
                    onClick={() => setShowSalary(false)}
                    style={{
                      marginBottom: 12,
                      padding: 0,
                      border: "none",
                      background: "none",
                      color: "#3399FF",
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer",
                      textDecoration: "underline"
                    }}
                  >
                    Voer bedrag direct in
                  </button>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <label style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Bruto jaarloon</label>
                    <Info text="Vul hier je bruto jaarloon in. DUO gebruikt het inkomen van 2 jaar geleden (peiljaar); wij schatten je maandbedrag op basis van dezelfde regels (4% of 12% boven de draagkrachtvrije voet)." />
                  </div>
                  <input type="number" min="0" step="100" value={yearlySalary}
                    onChange={(e) => setYearlySalary(Number(e.target.value))}
                    onFocus={() => setFocusedInput('yearlySalary')}
                    onBlur={() => setFocusedInput(null)}
                    placeholder="Bijv. 36000"
                    style={{
                      ...(focusedInput === 'yearlySalary' ? inputStyleFocusedWhite : inputStyleWhite),
                      width: "100%",
                      maxWidth: "none"
                    }} />
                  <div style={{ marginTop: 8, fontSize: 14, color: "#374151", fontWeight: 600 }}>
                    Minimale aflossing (maandbedrag): €{calculatedMinPayment}
                  </div>
                </div>
              )}
            </div>
          </div>
          </>
          )}
          {sidebarSection === 'aflosstrategie' && (
          <>
          {/* Maandbedrag aflossen */}
          <div style={{ marginTop: sidebarSection === 'aflosstrategie' ? 0 : 32 }}>
            <h2 style={{ marginTop: 0, marginBottom: 12, color: "#111827" }}>Maandbedrag aflossen</h2>
            <p style={{ 
              marginTop: 0, 
              marginBottom: 20, 
              fontSize: 14, 
              color: "#6b7280",
              lineHeight: 1.5
            }}>
              Stel je maandelijkse aflossing in en eventueel een jaarlijkse stijging. Alle berekeningen passen zich direct aan.
            </p>
            {/* Maandelijkse aflossing - same structure as Totale schuld */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#374151" }}>Maandelijkse aflossing (€)</label>
                <button
                  type="button"
                  onClick={handleMatchTerm}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    color: "#6b7280",
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer"
                  }}
                >
                  Match termijn
                </button>
              </div>
              <input
                type="number"
                min={0}
                max={2000}
                step={10}
                value={monthlyPayment === 0 ? '' : monthlyPayment}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || value === null || value === undefined) setMonthlyPayment(0);
                  else { const num = Number(value); if (!Number.isNaN(num)) setMonthlyPayment(num); }
                }}
                onFocus={() => setFocusedInput('monthlyPayment')}
                onBlur={() => {
                  setFocusedInput(null);
                  setMonthlyPayment((prev) => {
                    if (prev === '' || prev === null || prev === undefined) return 0;
                    const parsed = Number(prev);
                    if (Number.isNaN(parsed) || parsed < 0) return 0;
                    return Math.min(2000, parsed);
                  });
                }}
                placeholder="Bijv. 200"
                style={{
                  ...(focusedInput === 'monthlyPayment' ? inputStyleFocusedWhite : inputStyleWhite),
                  width: "100%",
                  maxWidth: "none"
                }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>Minimum: €{effectiveMinPayment}</span>
              </div>
              <BoxSlider
                min={effectiveMinPayment}
                max={1000}
                step={1}
                value={constrainedMonthlyPayment}
                onChange={(v) => setMonthlyPayment(v)}
              />
            </div>
            {/* Jaarlijkse stijging maandbedrag - always visible 0–5% */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>Jaarlijkse stijging maandbedrag</label>
                <Info text="Laat je maandelijkse aflossing jaarlijks met een vast percentage stijgen (bijv. door salarisgroei). Het percentage wordt elk jaar op het bedrag van het jaar ervoor toegepast." />
              </div>
              <div style={{ marginBottom: 8, fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
                0% — {(growthRate / 10).toFixed(1)}% per jaar — 5%
              </div>
              <BoxSlider min={0} max={50} step={1} value={growthRate} onChange={(v) => setGrowthRate(v)} />
            </div>
            </div>
          </>
          )}
          {sidebarSection === 'delay' && (
          <>
          {/* Aflossingen uitstellen */}
          <div style={{ marginTop: sidebarSection === 'delay' ? 0 : 32 }}>
            <h2 style={{ marginTop: 0, marginBottom: 12, color: "#111827" }}>Aflossingen uitstellen</h2>
            <p style={{ marginTop: 0, marginBottom: 20, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>
              Je mag maximaal 5 jaar het begin van je aflossing uitstellen. Tijdens deze periode loopt de rente wel door. Vul 0 jaar en 0 maanden in als je niet wilt uitstellen.
            </p>
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", margin: 0 }}>Uitstelduur</label>
                <Info text="Je mag maximaal 5 jaar je aflossing uitstellen. Tijdens deze periode loopt de rente wel door en wordt deze bij je schuld opgeteld. Voor meer informatie over uitstel en de voorwaarden kun je terecht op de website van DUO." />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8, display: "block" }}>Jaren</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <button type="button" onClick={() => { const newYears = Math.max(0, delayYears - 1); setDelayYears(newYears); if (newYears === 5) setDelayMonths(0); }}
                      style={{ width: "40px", height: "40px", border: "2px solid #e5e7eb", borderRight: "none", borderTopLeftRadius: 12, borderBottomLeftRadius: 12, background: "#f3f4f6", color: "#374151", fontSize: 18, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
                    <input type="number" min="0" max="5" value={delayYears} onChange={(e) => { const newYears = Math.max(0, Math.min(5, Number(e.target.value) || 0)); setDelayYears(newYears); if (newYears === 5) setDelayMonths(0); }}
                      style={{ ...inputStyleWhite, flex: 1, padding: "8px 12px", fontSize: 15, textAlign: "center", borderRadius: 0, borderLeft: "none", borderRight: "none" }} onWheel={(e) => e.target.blur()} />
                    <button type="button" onClick={() => { const newYears = Math.min(5, delayYears + 1); setDelayYears(newYears); if (newYears === 5) setDelayMonths(0); }}
                      style={{ width: "40px", height: "40px", border: "2px solid #e5e7eb", borderLeft: "none", borderTopRightRadius: 12, borderBottomRightRadius: 12, background: "#f3f4f6", color: "#374151", fontSize: 18, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8, display: "block" }}>Maanden</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                    <button type="button" onClick={() => { if (delayYears < 5) setDelayMonths(Math.max(0, delayMonths - 1)); }} disabled={delayYears === 5}
                      style={{ width: "40px", height: "40px", border: "2px solid #e5e7eb", borderRight: "none", borderTopLeftRadius: 12, borderBottomLeftRadius: 12, background: delayYears === 5 ? "#f9fafb" : "#f3f4f6", color: delayYears === 5 ? "#9ca3af" : "#374151", fontSize: 18, fontWeight: 600, cursor: delayYears === 5 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: delayYears === 5 ? 0.5 : 1 }}>−</button>
                    <input type="number" min="0" max={delayYears < 5 ? 11 : 0} value={delayMonths} onChange={(e) => { if (delayYears < 5) setDelayMonths(Math.max(0, Math.min(11, Number(e.target.value) || 0))); else setDelayMonths(0); }} disabled={delayYears === 5}
                      style={{ ...inputStyleWhite, flex: 1, padding: "8px 12px", fontSize: 15, textAlign: "center", borderRadius: 0, borderLeft: "none", borderRight: "none", opacity: delayYears === 5 ? 0.5 : 1, cursor: delayYears === 5 ? "not-allowed" : "text" }} onWheel={(e) => e.target.blur()} />
                    <button type="button" onClick={() => { if (delayYears < 5) setDelayMonths(Math.min(11, delayMonths + 1)); }} disabled={delayYears === 5}
                      style={{ width: "40px", height: "40px", border: "2px solid #e5e7eb", borderLeft: "none", borderTopRightRadius: 12, borderBottomRightRadius: 12, background: delayYears === 5 ? "#f9fafb" : "#f3f4f6", color: delayYears === 5 ? "#9ca3af" : "#374151", fontSize: 18, fontWeight: 600, cursor: delayYears === 5 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: delayYears === 5 ? 0.5 : 1 }}>+</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </>
          )}
            {sidebarSection === 'investeren' && (
            <>
            {/* Investeren naast aflossen */}
            <div style={{ marginTop: sidebarSection === 'investeren' ? 0 : 24 }}>
              <h2 style={{ marginTop: 0, marginBottom: 12, color: "#111827" }}>Investeren naast aflossen</h2>
              <p style={{ marginTop: 0, marginBottom: 16, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>
                Beleg (een deel van) je extra bedrag naast aflossen. Vul 0 in als je niet wilt investeren.
              </p>
              <div style={{ marginBottom: 16 }}>
                <button
                  type="button"
                  onClick={() => setEnableInvestment(!enableInvestment)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "none",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                    width: "auto",
                    minWidth: 100,
                    ...(enableInvestment
                      ? { background: "linear-gradient(135deg, #3399FF 0%, #2288dd 100%)", color: "#fff", boxShadow: "0 4px 12px rgba(83,113,255,0.3)" }
                      : { background: "#6b7280", color: "#fff" }),
                  }}
                >
                  {enableInvestment ? "Actief" : "Inactief"}
                </button>
              </div>
              <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <span 
                  onClick={() => setShowInvestmentExplanation(true)}
                  style={{ fontSize: 14, color: "#3399FF", textDecoration: "underline", cursor: "pointer" }}
                >
                  Toelichting
                </span>
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", margin: 0 }}>Maandelijkse investering (€)</label>
                </div>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={monthlyInvestment === 0 ? "" : monthlyInvestment}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMonthlyInvestment(val === "" ? 0 : Number(val));
                  }}
                  onFocus={() => setFocusedInput('monthlyInvestment')}
                  onBlur={() => setFocusedInput(null)}
                  placeholder="Bijv. 50"
                  style={{
                    ...(focusedInput === 'monthlyInvestment' ? inputStyleFocusedWhite : inputStyleWhite),
                    width: "100%",
                    maxWidth: "none",
                    marginBottom: 12
                  }}
                />
                <div style={{ marginBottom: 8, fontSize: 12, color: "#6b7280", fontWeight: 500 }}>0 — €{Math.min(monthlyInvestment, 500)} — €500</div>
                <BoxSlider min={0} max={500} step={1} value={Math.min(monthlyInvestment, 500)} onChange={(v) => setMonthlyInvestment(v)} />
            </div>
            {/* Startkapitaal investeringen */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", margin: 0 }}>Startkapitaal investeringen (optioneel)</label>
                <Info text="Heb je al geld belegd in aandelen, indexfondsen of andere investeringen? Voer hier het totale bedrag in dat je nu al hebt staan. Dit startbedrag groeit mee met de investeringen die je maandelijks doet, waardoor je sneller je lening kunt afbetalen. Als je nog geen investeringen hebt, laat je dit veld leeg." />
              </div>
              <input
                type="number"
                min="0"
                step="100"
                value={startingInvestmentCapital === "" || startingInvestmentCapital === 0 ? "" : startingInvestmentCapital}
                onChange={(e) => {
                  const val = e.target.value;
                  setStartingInvestmentCapital(val === "" ? "" : Number(val) || "");
                }}
                onFocus={() => setFocusedInput('startingInvestmentCapital')}
                onBlur={() => setFocusedInput(null)}
                placeholder=""
                style={{
                  ...(focusedInput === 'startingInvestmentCapital' ? inputStyleFocusedWhite : inputStyleWhite),
                  width: "100%",
                  maxWidth: "none"
                }}
              />
              <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
                Heb je al een investeringsportefeuille? Voer hier het startbedrag in.
              </div>
            </div>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#374151", margin: 0 }}>Jaarlijkse stijging investeringen</label>
                <Info text="Laat je maandelijkse inleg jaarlijks met een vast percentage stijgen. Je kunt dit onafhankelijk van de stijging van je aflossingen instellen." />
              </div>
              <label style={{ 
                display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderRadius: 12,
                background: enableSeparateGrowth ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.05)",
                border: enableSeparateGrowth ? "2px solid rgba(255,255,255,0.3)" : "2px solid rgba(255,255,255,0.1)",
                cursor: "pointer", fontSize: 14, fontWeight: 500, marginBottom: 12
              }}>
                <input type="checkbox" checked={enableSeparateGrowth} onChange={(e) => setEnableSeparateGrowth(e.target.checked)} 
                  style={{ accentColor: "#3399FF", transform: "scale(1.3)", cursor: "pointer" }} />
                Laat investeringen jaarlijks stijgen
              </label>
              {enableSeparateGrowth && (
                <>
                  <div style={{ marginBottom: 8, fontSize: 12, color: "#6b7280", fontWeight: 500 }}>0% — {(investmentGrowthRate / 10).toFixed(1)}% per jaar — 5%</div>
                  <BoxSlider min={0} max={50} step={1} value={investmentGrowthRate} onChange={(v) => setInvestmentGrowthRate(v)} />
                </>
              )}
            </div>
            </div>
            </>
            )}
            {sidebarSection === 'eenmalig' && (
            <>
            {/* Eenmalige aflossingen */}
            <div style={{ marginTop: sidebarSection === 'eenmalig' ? 0 : 24 }}>
              <h2 style={{ marginTop: 0, marginBottom: 12, color: "#111827" }}>Eenmalige aflossingen</h2>
              <p style={{ marginTop: 0, marginBottom: 16, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>
                Voeg eenmalige aflossingen toe die je in één keer op een bepaald moment doet. Bijvoorbeeld een erfenis of bonus.
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <Info text="Voeg eenmalige aflossingen toe die je in één keer op een bepaald moment doet. Bijvoorbeeld een erfenis of bonus die je wilt inzetten om je studieschuld te verlagen." />
              </div>
              {oneTimeRepayments.map((r, idx) => (
                <div key={idx} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                  <input
                    type="number"
                    min={0}
                    step={100}
                    value={r.amount === 0 ? "" : r.amount}
                    onChange={(e) => {
                      const v = e.target.value;
                      const amt = v === "" ? 0 : Math.max(0, Number(v));
                      setOneTimeRepayments((prev) => prev.map((x, i) => (i === idx ? { ...x, amount: amt } : x)));
                    }}
                    onFocus={() => setFocusedInput('oneTime-' + idx)}
                    onBlur={() => setFocusedInput(null)}
                    placeholder="Bedrag (€)"
                    style={{
                      ...(focusedInput === 'oneTime-' + idx ? inputStyleFocusedWhite : inputStyleWhite),
                      flex: 1,
                      minWidth: 0,
                      maxWidth: "none"
                    }}
                  />
                  <select
                    value={r.year === 0 ? 0 : (r.year || 1)}
                    onChange={(e) => {
                      const val = e.target.value;
                      const yr = val === "0" ? 0 : Math.max(1, Math.min(totalTermYears, Number(val)));
                      setOneTimeRepayments((prev) => prev.map((x, i) => (i === idx ? { ...x, year: yr } : x)));
                    }}
                    style={{
                      ...inputStyleWhite,
                      width: "auto",
                      minWidth: "5.5em",
                      padding: "10px 10px",
                      textAlign: "right",
                      flexShrink: 0
                    }}
                  >
                    <option value={0}>Jaarlijks</option>
                    {Array.from({ length: Math.max(1, totalTermYears) }, (_, i) => i + 1).map((y) => (
                      <option key={y} value={y}>Jaar {y}</option>
                    ))}
                  </select>
                </div>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setOneTimeRepayments((prev) => [...prev, { amount: 0, year: 1 }])}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    border: "1px solid #d1d5db",
                    background: "#e5e7eb",
                    color: "#374151",
                    fontSize: 24,
                    fontWeight: 400,
                    cursor: "pointer",
                    padding: 0,
                    lineHeight: 1,
                  }}
                  aria-label="Nog een eenmalige aflossing toevoegen"
                  title="Nog een eenmalige aflossing toevoegen"
                >
                  +
                </button>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>Nog een toevoegen</span>
              </div>
            </div>
            </>
            )}
            {sidebarSection === 'feedback' && (
            <>
            {/* Geef feedback – losse categorie onder Eenmalige aflossingen */}
            <div style={{ marginTop: 0 }}>
              <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 12, marginBottom: 12 }}>
                <h2 style={{ marginTop: 0, marginBottom: 8, color: "#111827" }}>Geef feedback</h2>
                <p style={{ marginTop: 0, marginBottom: 12, fontSize: 14, color: "#6b7280", lineHeight: 1.5 }}>
                  Laat ons weten wat je van de portal vindt. Jouw input helpt ons verbeteren.
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6, fontFamily: "inherit" }}>Studierichting</label>
                  <select
                    value={sidebarFeedbackStudierichting}
                    onChange={(e) => setSidebarFeedbackStudierichting(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      fontSize: 15,
                      fontFamily: "inherit",
                      background: "#fff",
                      color: "#1f2937",
                      outline: "none",
                      cursor: "pointer",
                      boxSizing: "border-box"
                    }}
                  >
                    <option value="">Selecteer studierichting</option>
                    {SIDEBAR_STUDIERICHTINGEN.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                {sidebarFeedbackStudierichting === "Anders" && (
                  <div>
                    <input
                      type="text"
                      value={sidebarFeedbackAnders}
                      onChange={(e) => setSidebarFeedbackAnders(e.target.value)}
                      placeholder="Vul je studierichting in"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        fontSize: 15,
                        fontFamily: "inherit",
                        outline: "none",
                        boxSizing: "border-box"
                      }}
                    />
                  </div>
                )}
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6, fontFamily: "inherit" }}>Jouw feedback</label>
                  <textarea
                    value={sidebarFeedbackText}
                    onChange={(e) => setSidebarFeedbackText(e.target.value)}
                    placeholder="Wat vond je van deze module? Wat kon er beter?"
                    rows={4}
                    style={{
                      width: "100%",
                      resize: "vertical",
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: "10px 12px",
                      boxSizing: "border-box",
                      fontSize: 15,
                      fontFamily: "inherit",
                      outline: "none"
                    }}
                  />
                </div>
                <button
                  type="button"
                  disabled={sidebarFeedbackStatus === "sending" || !sidebarFeedbackText.trim()}
                  onClick={() => {
                    if (!sidebarFeedbackText.trim()) return;
                    setSidebarFeedbackStatus("sending");
                    const finalStudierichting = sidebarFeedbackStudierichting === "Anders" ? sidebarFeedbackAnders : sidebarFeedbackStudierichting;
                    logUserActivity("feedback", { page: "PortalPage", feedback: sidebarFeedbackText.trim(), studierichting: finalStudierichting });
                    setSidebarFeedbackStatus("success");
                    setSidebarFeedbackText("");
                    setSidebarFeedbackStudierichting("");
                    setSidebarFeedbackAnders("");
                  }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    background: sidebarFeedbackStatus === "success" ? "#10b981" : "linear-gradient(135deg, #e0efff 0%, #cce0ff 100%)",
                    color: sidebarFeedbackStatus === "success" ? "#fff" : "#374151",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: sidebarFeedbackStatus === "sending" || !sidebarFeedbackText.trim() ? "not-allowed" : "pointer",
                    opacity: sidebarFeedbackStatus === "sending" || !sidebarFeedbackText.trim() ? 0.7 : 1
                  }}
                >
                  {sidebarFeedbackStatus === "sending" ? "Bezig…" : sidebarFeedbackStatus === "success" ? "Verstuurd" : "Verstuur feedback"}
                </button>
              </div>
            </div>
            </>
            )}
          </div>
            )}
          </aside>
          )}

          {/* Right area: summary + chart (no outer box, on grey). Inner wrapper keeps same width when sidebar collapsed. Or saved list / strategy summary when USE_UNIFIED_TOP_BAR. */}
        <div
          ref={rightPanelScrollRef}
          style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          padding: 24,
          paddingBottom: 48,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflowY: shouldBlurRight ? "hidden" : "auto",
          position: "relative",
          overflowX: "hidden"
        }}
        >
          {mainView === "portal" && (
          <div
            ref={rightAreaRefCallback}
            style={{
              width: "100%",
              maxWidth: `calc(100vw - ${SIDEBAR_WIDTH_EXPANDED + 48}px)`,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              paddingBottom: 24,
            }}
          >
          {/* Summary: blue tiles (full width to right edge of graph; graph is 16px inset in card) */}
          <div ref={guideSummarySectionRef} style={{ marginBottom: 16 }}>
            <div ref={guideSummaryHeadingRef} style={{ marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: "#111827" }}>Samenvatting</h2>
            </div>
            <div style={{ width: chartWidth ? `${chartWidth + 80}px` : "100%", minWidth: 0, boxSizing: "border-box", display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
              <div ref={(el) => { guideSummaryTileRefs.current[0] = el; }} style={SUMMARY_TILE_STYLE}>
                <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.95, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>Totale kosten</span>
                  <InfoSummary text="De totale kosten zijn de som van alle maandelijkse betalingen die je doet binnen de aangegeven looptijd. Dit omvat zowel de aflossing van je studieschuld, de betaalde rente, als de totale investeringen (exclusief groei)." />
                </div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>€ {Math.round(totalCostWithInvestments).toLocaleString("nl-NL")}</div>
              </div>
              <div ref={(el) => { guideSummaryTileRefs.current[1] = el; }} style={SUMMARY_TILE_STYLE}>
                <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.95, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>Betaald aan rente</span>
                  <InfoSummary text="Dit is het totaalbedrag aan rente dat je betaalt binnen de aangegeven looptijd. De rente wordt maandelijks berekend over je resterende studieschuld." />
                </div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>€ {Math.round(interestAtEnd).toLocaleString("nl-NL")}</div>
              </div>
              <div ref={(el) => { guideSummaryTileRefs.current[2] = el; }} style={SUMMARY_TILE_STYLE}>
                <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.95, marginBottom: 6 }}>Stelsel</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{effectivePlanYears} jaar</div>
              </div>
              <div ref={(el) => { guideSummaryTileRefs.current[3] = el; }} style={SUMMARY_TILE_STYLE}>
                <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.95, marginBottom: 6 }}>Strategie</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {enableInvestment ? 
                    (() => {
                      const availableForInvestment = Math.max(0, constrainedMonthlyPayment - effectiveMinPayment);
                      const investmentRatio = availableForInvestment > 0 ? (monthlyInvestment / availableForInvestment) : 0;
                      return investmentRatio >= 0.8 ? 'Focus investeren' : 
                             investmentRatio >= 0.2 ? 'Combinatie' : 'Focus aflossen';
                    })() :
                    'Volledig aflossen'}
                </div>
              </div>
              <div ref={(el) => { guideSummaryTileRefs.current[4] = el; }} style={SUMMARY_TILE_STYLE}>
                <div style={{ fontSize: 15, fontWeight: 600, opacity: 0.95, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span>Looptijd</span>
                  <InfoSummary text="De looptijd is de tijd die je nodig hebt om je studieschuld volledig af te lossen. Als je investeert en je investeringen de resterende schuld overtreffen, kun je de lening eerder aflossen." />
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: (payoffMonths >= effectivePlanYears * 12 && schedule[schedule.length - 1]?.balance > 0 && !result.payoffByInvestment) ? "#FEE2E2" : undefined }}>
                  {(() => {
                    const finalBalance = schedule[schedule.length - 1]?.balance || 0;
                    const isNotPaidOff = payoffMonths >= effectivePlanYears * 12 && finalBalance > 0 && !result.payoffByInvestment;
                    const isExactTerm = Math.abs(payoffMonths - effectivePlanYears * 12) < 0.5 && (finalBalance <= 0.01 || result.payoffByInvestment);
                    if (isNotPaidOff) return "Niet binnen termijn";
                    if (isExactTerm) return `Exact ${effectivePlanYears} jaar`;
                    return payoffRemainderMonths > 0 ? `${payoffYears} jaar ${payoffRemainderMonths} mnd` : `${payoffYears} jaar`;
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Verloop studieschuld */}
          <div ref={guideVerloopBlockRef} style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 16, marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Verloop studieschuld</h2>
            </div>
            {/* Legend */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "24px", 
              marginBottom: "16px",
              paddingBottom: "12px",
              borderBottom: "1px solid #E5E7EB"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "24px", height: "3px", backgroundColor: "#3b5bfd", borderRadius: "2px" }}></div>
                <span style={{ fontSize: "14px", color: "#374151", fontWeight: 500 }}>Openstaande schuld</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "24px", height: "2px", backgroundColor: "#94a3b8" }}></div>
                <span style={{ fontSize: "14px", color: "#374151", fontWeight: 500 }}>Betaalde rente</span>
              </div>
              {enableInvestment && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "24px", height: "2px", backgroundColor: "#10b981" }}></div>
                  <span style={{ fontSize: "14px", color: "#374151", fontWeight: 500 }}>Investeringen</span>
                </div>
              )}
            </div>
            {showInvestmentPayoffBadge && (
              <div
                style={{
                  marginTop: 10,
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: "#eff6ff",
                  border: "1px solid #93c5fd",
                  fontSize: "14px",
                  color: "#1e40af",
                  fontWeight: 500,
                }}
              >
                Lening afbetaald via investering
              </div>
            )}
            <div style={{ width: chartWidth ? `${chartWidth}px` : "100%", minWidth: 0, boxSizing: "border-box" }}>
              <PortalChart
              showPayoffLabelInChart={false}
              showInvestmentPayoffLabel={showInvestmentPayoffBadge}
              data={yearlyData}
              hoverYear={sharedHoverYear}
              setHoverYear={setSharedHoverYear}
              enableInvestment={enableInvestment}
              payoffByInvestment={payoffByInvestment}
              payoffMonths={payoffMonths}
              enableDelay={enableDelay}
              delayYears={delayYears}
              delayMonths={delayMonths}
              totalTermYears={totalTermYears}
              totalTermMonths={totalTermMonths}
              planYears={effectivePlanYears}
              chartHeight={300}
            />
            </div>
          </div>

          {/* Betaalschema */}
          <div ref={guideBetaalBlockRef} style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 16, marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Betaalschema</h2>
            </div>
            {/* Legend */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "24px", 
              marginBottom: "16px",
              paddingBottom: "12px",
              borderBottom: "1px solid #E5E7EB"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "24px", height: "3px", backgroundColor: "#3399FF", borderRadius: "2px" }}></div>
                <span style={{ fontSize: "14px", color: "#374151", fontWeight: 500 }}>Maandelijkse aflossing</span>
              </div>
              {enableInvestment && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "24px", height: "3px", backgroundColor: "#10b981", borderRadius: "2px" }}></div>
                  <span style={{ fontSize: "14px", color: "#374151", fontWeight: 500 }}>Maandelijkse investeringen</span>
                </div>
              )}
            </div>
            <div style={{ width: chartWidth ? `${chartWidth}px` : "100%", minWidth: 0, boxSizing: "border-box" }}>
              <PaymentChart data={yearlyData} hoverYear={sharedHoverYear} setHoverYear={setSharedHoverYear} />
            </div>
          </div>
          </div>
          )}

          {mainView === "saved" && (
            <div style={{ flex: 1, overflowY: "auto", width: "100%" }}>
              <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
                <h1 style={{ margin: 0, marginBottom: 24, fontSize: 24, fontWeight: 700, color: "#111827" }}>Opgeslagen strategieën</h1>
                {savedStrategies.length === 0 ? (
                  <p style={{ color: "#6b7280", fontSize: 15 }}>Nog geen strategieën opgeslagen. Sla je huidige instellingen op via &quot;Sla strategie op&quot;.</p>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {savedStrategies.map((s) => (
                      <li
                        key={s.id}
                        style={{
                          padding: "20px 24px",
                          background: "#f9fafb",
                          borderRadius: 16,
                          marginBottom: 16,
                          display: "flex",
                          flexDirection: "column",
                          gap: 16,
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                          <div>
                            <div style={{ fontWeight: 600, color: "#1f2937", fontSize: 17 }}>{s.name}</div>
                            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                              {s.timestamp ? new Date(s.timestamp).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : ""}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              onClick={() => {
                                setLoadedStrategy(s);
                                setTotalDebt(s.totalDebt ?? 23000);
                                setRepaymentPlan(String(s.repaymentPlan ?? 15));
                                setMinPayment(s.minPayment ?? 0);
                                setMonthlyPayment(s.monthlyPayment ?? 250);
                                setEnableGrowth(!!s.enableGrowth);
                                setGrowthRate(s.growthRate ?? 0);
                                setEnableSeparateGrowth(!!s.enableSeparateGrowth);
                                setInvestmentGrowthRate(s.investmentGrowthRate ?? 0);
                                setEnableInvestment(!!s.enableInvestment);
                                setMonthlyInvestment(s.monthlyInvestment ?? 0);
                                setEnableDelay(!!s.enableDelay);
                                setDelayYears(s.delayYears ?? 0);
                                setDelayMonths(s.delayMonths ?? 0);
                                setStartingInvestmentCapital(s.startingInvestmentCapital ? String(s.startingInvestmentCapital) : "");
                                setHasStartedRepaying(!!s.hasStartedRepaying);
                                setYearsAlreadyRepaid(s.yearsAlreadyRepaid ?? 0);
                                setOneTimeRepayments(Array.isArray(s.oneTimeRepayments) && s.oneTimeRepayments.length > 0 ? s.oneTimeRepayments : [{ amount: 0, year: 1 }]);
                                setEnableCustomRates(!!s.enableCustomRates);
                                setCustomRates(Array.isArray(s.customRates) && s.customRates.length > 0 ? s.customRates : []);
                                setPendingNavigateToSummary(true);
                              }}
                              style={{
                                padding: "10px 18px",
                                borderRadius: 10,
                                border: "none",
                                background: "#374151",
                                color: "#fff",
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              Samenvatting & aflosschema
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setLoadedStrategy(s);
                                setTotalDebt(s.totalDebt ?? 23000);
                                setRepaymentPlan(String(s.repaymentPlan ?? 15));
                                setMinPayment(s.minPayment ?? 0);
                                setMonthlyPayment(s.monthlyPayment ?? 250);
                                setEnableGrowth(!!s.enableGrowth);
                                setGrowthRate(s.growthRate ?? 0);
                                setEnableSeparateGrowth(!!s.enableSeparateGrowth);
                                setInvestmentGrowthRate(s.investmentGrowthRate ?? 0);
                                setEnableInvestment(!!s.enableInvestment);
                                setMonthlyInvestment(s.monthlyInvestment ?? 0);
                                setEnableDelay(!!s.enableDelay);
                                setDelayYears(s.delayYears ?? 0);
                                setDelayMonths(s.delayMonths ?? 0);
                                setStartingInvestmentCapital(s.startingInvestmentCapital ? String(s.startingInvestmentCapital) : "");
                                setHasStartedRepaying(!!s.hasStartedRepaying);
                                setYearsAlreadyRepaid(s.yearsAlreadyRepaid ?? 0);
                                setOneTimeRepayments(Array.isArray(s.oneTimeRepayments) && s.oneTimeRepayments.length > 0 ? s.oneTimeRepayments : [{ amount: 0, year: 1 }]);
                                setEnableCustomRates(!!s.enableCustomRates);
                                setCustomRates(Array.isArray(s.customRates) && s.customRates.length > 0 ? s.customRates : []);
                                setMainView("portal");
                              }}
                              style={{
                                padding: "10px 18px",
                                borderRadius: 10,
                                border: "none",
                                ...PORTAL_GRADIENT,
                                color: "#fff",
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                            >
                              Laad in portal
                            </button>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm(`Weet je zeker dat je "${s.name}" wilt verwijderen?`)) {
                              await deletePortalStrategy(s.id, uid);
                              const list = await getSavedStrategies(uid);
                              setSavedStrategies(list);
                            }
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            fontSize: 13,
                            color: "#6b7280",
                            cursor: "pointer",
                            alignSelf: "flex-start",
                            textDecoration: "underline",
                          }}
                        >
                          Verwijderen
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {mainView === "faq" && (() => {
            const faqItems = (landingConfig.sections || []).find((s) => s.type === "faq" && s.id === "faq")?.items || [];
            const toggleFaq = (idx) => {
              setFaqOpenIndices((prev) =>
                prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
              );
            };
            const renderFaqAnswer = (text) => {
              const hasDisclaimer = text.includes("**BELANGRIJK:");
              const parts = hasDisclaimer ? text.split("**BELANGRIJK:") : [text];
              return (
                <>
                  {parts[0].split(/(\*[^*\n]+\*)/g).map((part, i) => {
                    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
                      return <em key={i} style={{ fontStyle: "italic" }}>{part.slice(1, -1)}</em>;
                    }
                    return <span key={i}>{part}</span>;
                  })}
                  {hasDisclaimer && (
                    <div style={{
                      marginTop: 12,
                      padding: 12,
                      background: "#DBEAFE",
                      border: "2px solid #93C5FD",
                      borderRadius: 8,
                      color: "#1E40AF",
                      fontWeight: 600,
                      fontSize: 14
                    }}>
                      BELANGRIJK: {parts[1].replace(/\*\*/g, "")}
                    </div>
                  )}
                </>
              );
            };
            return (
              <div style={{ flex: 1, overflowY: "auto", width: "100%" }}>
                <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
                  <h1 style={{ margin: 0, marginBottom: 32, fontSize: 28, fontWeight: 700, color: "#111827" }}>FAQ</h1>
                  <div style={{ display: "flex", gap: 20 }}>
                    {[0, 1].map((col) => (
                      <div
                        key={col}
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 20,
                          alignItems: "stretch"
                        }}
                      >
                        {faqItems
                          .map((qa, idx) => ({ qa, idx }))
                          .filter((_, i) => i % 2 === col)
                          .map(({ qa, idx }) => {
                            const isOpen = faqOpenIndices.includes(idx);
                            return (
                              <div
                                key={idx}
                                style={{
                                  background: "#FFFFFF",
                                  border: "1px solid #E5E7EB",
                                  borderRadius: 16,
                                  overflow: "hidden",
                                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  flexDirection: "column"
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => toggleFaq(idx)}
                                  style={{
                                    width: "100%",
                                    padding: "18px 20px",
                                    cursor: "pointer",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    fontWeight: 600,
                                    fontSize: 16,
                                    color: "#1F2937",
                                    background: "#fff",
                                    border: "none",
                                    textAlign: "left",
                                    fontFamily: "inherit",
                                    transition: "background-color 0.2s ease",
                                    minHeight: 56,
                                    boxSizing: "border-box"
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#F9FAFB";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "#fff";
                                  }}
                                >
                                  <span style={{ lineHeight: 1.4, flex: 1, paddingRight: 12 }}>{qa.q}</span>
                                  <span style={{
                                    fontSize: 20,
                                    color: "#3399FF",
                                    transition: "transform 0.3s ease",
                                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                    display: "inline-block",
                                    flexShrink: 0
                                  }}>
                                    ▼
                                  </span>
                                </button>
                                {isOpen && (
                                  <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      padding: "0 20px 20px 20px",
                                      color: "#4B5563",
                                      lineHeight: 1.6,
                                      whiteSpace: "pre-line",
                                      fontSize: 15,
                                      background: "#fff"
                                    }}
                                  >
                                    {renderFaqAnswer(qa.a)}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {mainView === "strategySummary" && loadedStrategy && (() => {
            const c = summaryCalc;
            const totalTermYears = c.effectivePlanYears + (c.enableDelay ? c.delayYears : 0);
            const totalTermMonths = totalTermYears * 12;
            const finalBalance = c.schedule?.length ? (c.schedule[c.schedule.length - 1]?.balance ?? 0) : 0;
            const isNotPaidOffWithinTerm = c.payoffMonths >= totalTermMonths && finalBalance > 0 && !c.payoffByInvestment;
            const gr = c.growthRate / 1000;
            const igr = c.enableSeparateGrowth ? c.investmentGrowthRate / 1000 : gr;
            const delayMonthsTotal = c.enableDelay ? c.delayYears * 12 + c.delayMonths : 0;
            const maxYearsForData = c.enableDelay && (c.delayYears > 0 || c.delayMonths > 0) ? totalTermYears : c.effectivePlanYears;
            const oneTimeReps = c.oneTimeRepayments || [];
            const summaryYearlyTableData = [];
            const payoffYearCap = Math.max(0, Math.floor((c.payoffMonths - 1) / 12)); // Laatste jaar-index (0-based)
            for (let y = 0; y <= maxYearsForData; y++) {
              if (y > payoffYearCap) break;
              const yearEndMonth = Math.min((y + 1) * 12, c.payoffMonths);
              const s = sampleAtMonth(c.schedule, yearEndMonth);
              let balanceAtEnd = Math.max(0, s.balance ?? 0);
              const invValue = s.investmentValue ?? 0;
              if (c.enableInvestment && invValue > balanceAtEnd && balanceAtEnd > 0.01) {
                balanceAtEnd = 0;
              }
              if (y === payoffYearCap) balanceAtEnd = 0; // Laatste rij = aflossingsjaar, altijd 0 schuld
              const yearsElapsed = Math.max(0, y - delayMonthsTotal / 12);
              let monthlyPay = 0;
              let monthlyInv = 0;
              if ((y + 1) * 12 > delayMonthsTotal) {
                monthlyPay = c.constrainedMonthlyPayment * (c.enableGrowth ? Math.pow(1 + gr, Math.floor(yearsElapsed)) : 1);
                monthlyInv = c.enableInvestment ? c.monthlyInvestment * (c.enableSeparateGrowth ? Math.pow(1 + igr, Math.floor(yearsElapsed)) : (c.enableGrowth ? Math.pow(1 + gr, Math.floor(yearsElapsed)) : 1)) : 0;
              }
              const oneTimeThisYear = oneTimeReps
                .filter((r) => r && (r.amount || 0) > 0)
                .filter((r) => r.year === 0 || (r.year || 0) === y + 1)
                .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
              summaryYearlyTableData.push({ year: y + 1, monthlyPayment: Math.round(monthlyPay), monthlyInvestment: Math.round(monthlyInv), oneTimeThisYear: Math.round(oneTimeThisYear), investmentValue: Math.round(s.investmentValue ?? 0), interestPaid: Math.round(s.interestCumulative ?? 0), balance: Math.round(balanceAtEnd) });
              if (balanceAtEnd <= 0.01) break;
              if (c.enableInvestment && invValue > (s.balance ?? 0) && (s.balance ?? 0) > 0.01) break;
              if (yearEndMonth >= c.payoffMonths) break;
            }
            const summaryContent = {
              payment: Math.round(c.constrainedMonthlyPayment),
              debt: Math.round(c.totalDebt),
              growthPct: (c.growthRate / 10).toFixed(1),
              investmentGrowthPct: (c.investmentGrowthRate / 10).toFixed(1),
              delayYears: c.delayYears,
              delayMonths: c.delayMonths,
              investment: Math.round(c.monthlyInvestment),
              totalTermYears,
              payoffYears: c.payoffYears,
              payoffRemainderMonths: c.payoffRemainderMonths,
              interestTotal: Math.round(c.interestAtEnd ?? 0),
              finalInv: Math.round(c.finalInvestmentValue ?? 0),
              hasGrowth: c.enableGrowth && c.growthRate > 0,
              hasInvestmentGrowth: c.enableSeparateGrowth && c.investmentGrowthRate > 0,
              hasDelay: c.enableDelay && (c.delayYears > 0 || c.delayMonths > 0),
              hasInvestment: c.enableInvestment && c.monthlyInvestment > 0,
              isNotPaidOff: isNotPaidOffWithinTerm,
            };
            const fmtVal = (val, fmt) => {
              if (val == null || (typeof val === "number" && isNaN(val))) return "—";
              if (fmt === "eur") return `€ ${Math.round(val).toLocaleString("nl-NL")}`;
              return String(val);
            };
            const TILE_IDS = [
              { id: "debt", label: "Studieschuld", valueKey: "totalDebt", fmt: "eur" },
              { id: "monthly", label: "Maandelijkse aflossing", valueKey: "constrainedMonthlyPayment", fmt: "eur" },
              { id: "years", label: "Jaar tot aflossing", valueKey: "payoffYears", fmt: null },
              { id: "totalCost", label: "Totale kosten", valueKey: "totalCostWithInvestments", fmt: "eur" },
              { id: "interest", label: "Rente betaald", valueKey: "interestAtEnd", fmt: "eur" },
              { id: "investment", label: "Waarde investeringen op einde looptijd", valueKey: "finalInvestmentValue", fmt: "eur" },
            ];
            const tileVals = { totalDebt: c.totalDebt, constrainedMonthlyPayment: c.constrainedMonthlyPayment, payoffYears: c.payoffYears, totalCostWithInvestments: c.totalCostWithInvestments, interestAtEnd: c.interestAtEnd, finalInvestmentValue: c.finalInvestmentValue };
            return (
              <div style={{ flex: 1, overflowY: "auto", width: "100%" }}>
                <div style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px" }}>
                  <button type="button" onClick={() => setMainView("saved")} style={{ marginBottom: 24, background: "none", border: "1px solid #d1d5db", borderRadius: 10, fontSize: 14, color: "#374151", padding: "10px 18px", cursor: "pointer" }}>
                    ← Terug naar opgeslagen strategieën
                  </button>
                  <h1 style={{ margin: "0 0 4px 0", fontSize: 28, fontWeight: 700, color: "#111827" }}>Samenvatting Strategie</h1>
                  <p style={{ fontSize: 15, color: "#6b7280", marginBottom: 24 }}>{loadedStrategy.name}</p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                    {TILE_IDS.map((t) => {
                      const val = tileVals[t.valueKey];
                      const isYearsTile = t.valueKey === "payoffYears";
                      const isInvestmentTile = t.valueKey === "finalInvestmentValue";
                      const yearsDisplay = isYearsTile && isNotPaidOffWithinTerm ? "Niet binnen termijn" : isYearsTile ? (c.payoffRemainderMonths > 0 ? `${c.payoffYears} jaar ${c.payoffRemainderMonths} mnd` : `${c.payoffYears} jaar`) : null;
                      const valueDisplay = isYearsTile ? yearsDisplay : isInvestmentTile && (val == null || !c.enableInvestment) ? "—" : fmtVal(val, t.fmt);
                      return (
                        <div key={t.id} style={{ ...PORTAL_GRADIENT, borderRadius: 16, padding: 20, color: "#fff", boxShadow: `0 4px 12px ${PORTAL_ACCENT_RGBA(0.25)}` }}>
                          <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 4 }}>{t.label}</div>
                          <div style={{ fontSize: 22, fontWeight: 700 }}>{valueDisplay}</div>
                          {t.valueKey === "constrainedMonthlyPayment" && c.enableGrowth && <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>+{(c.growthRate / 10).toFixed(1)}%/jr stijging</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ padding: 20, background: "#f9fafb", borderRadius: 16, fontSize: 15, lineHeight: 1.7, color: "#374151", marginBottom: 24 }}>
                    <p style={{ margin: "0 0 12px 0" }}><strong>Wat is de strategie?</strong> Je lost <strong>€{summaryContent.payment.toLocaleString("nl-NL")} per maand</strong> af op je studieschuld van <strong>€{summaryContent.debt.toLocaleString("nl-NL")}</strong>.
                      {summaryContent.hasGrowth && <> Je verhoogt je aflossing <strong>jaarlijks met {summaryContent.growthPct}%</strong>.</>}
                      {summaryContent.hasDelay && <> Je stelt de aflossing <strong>{summaryContent.delayYears > 0 && summaryContent.delayMonths > 0 ? `${summaryContent.delayYears} jaar en ${summaryContent.delayMonths} maanden` : summaryContent.delayYears > 0 ? `${summaryContent.delayYears} jaar` : `${summaryContent.delayMonths} maanden`} uit</strong>.</>}
                      {summaryContent.hasInvestment && <> Daarnaast investeer je <strong>€{summaryContent.investment.toLocaleString("nl-NL")} per maand</strong>.{summaryContent.hasInvestmentGrowth && <> Ook je inleg laat je <strong>jaarlijks met {summaryContent.investmentGrowthPct}%</strong> stijgen.</>}</>}
                    </p>
                    <p style={{ margin: 0 }}><strong>Wat is de uitkomst?</strong>
                      {summaryContent.isNotPaidOff ? <> Binnen de looptijd van <strong>{summaryContent.totalTermYears} jaar</strong> wordt de lening <strong>niet</strong> afgelost.</> : <> De lening is afgelost in <strong>{summaryContent.payoffRemainderMonths > 0 ? `${summaryContent.payoffYears} jaar en ${summaryContent.payoffRemainderMonths} maanden` : `${summaryContent.payoffYears} jaar`}</strong>. Je betaalt in totaal <strong>€{summaryContent.interestTotal.toLocaleString("nl-NL")}</strong> aan rente.{summaryContent.hasInvestment && summaryContent.finalInv > 0 && <> De waarde van je investeringen aan het eind bedraagt <strong>€{summaryContent.finalInv.toLocaleString("nl-NL")}</strong>.</>}</>}
                    </p>
                  </div>
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1f2937", marginBottom: 12 }}>Aflosschema</h2>
                  <div style={{ overflowX: "auto", marginBottom: 12 }}>
                    <table style={{ width: "100%", minWidth: 600, borderCollapse: "collapse", fontSize: 14, tableLayout: "fixed" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "8%" }}>Jaar</th>
                          <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "18%" }}>Maandelijkse aflossing</th>
                          <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "18%" }}>Maandelijkse investering</th>
                          <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "16%" }}>Eenmalige aflossingen</th>
                          <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "16%" }}>Waarde investeringen (eind)</th>
                          <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "14%" }}>Totaal betaalde rente</th>
                          <th style={{ textAlign: "right", padding: "10px 8px", background: "#3399FF", color: "#fff", fontWeight: 600, width: "14%" }}>Restant schuld</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryYearlyTableData.map((row) => (
                          <tr key={row.year} style={{ borderBottom: "1px solid #f3f4f6" }}>
                            <td style={{ padding: "8px", color: "#1f2937", fontWeight: 500 }}>{row.year}</td>
                            <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.monthlyPayment ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.monthlyInvestment ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.oneTimeThisYear ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.investmentValue ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.interestPaid ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                            <td style={{ padding: "8px", textAlign: "right", color: "#374151" }}>€{(row.balance ?? 0).toLocaleString("nl-NL", { maximumFractionDigits: 0 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}><strong>Betaalschema</strong>: laat het verloop van je maandelijkse betaling zien (incl. jaarlijkse stijging als je die inschakelt). De lijn stopt zodra de lening is afbetaald.</p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      </div>
      </>
      )}

      {isGuideRightSpotlightPhase &&
      guideSpotlightComplementPanels.length > 0 &&
      typeof document !== "undefined"
        ? ReactDOM.createPortal(
            <>
              <TutorialComplementBlurStack
                panels={guideSpotlightComplementPanels}
                zBackdrop={PC_GUIDE_SPOTLIGHT_Z_BACKDROP}
                zBlock={PC_GUIDE_SPOTLIGHT_Z_BLOCK}
                backdropBlur={PC_GUIDE_SPOTLIGHT_BACKDROP_BLUR}
                overlayBackground={PC_GUIDE_SPOTLIGHT_OVERLAY_BG}
              />
              {guideChartCard && guideTutorialCardLayout ? (
                <div
                  style={{
                    position: "fixed",
                    ...(guideTutorialCardLayout.placement === "below"
                      ? { top: guideTutorialCardLayout.top, bottom: "auto" }
                      : guideTutorialCardLayout.placement === "leftSide"
                        ? { top: guideTutorialCardLayout.top, bottom: "auto" }
                        : { bottom: guideTutorialCardLayout.bottom, top: "auto" }),
                    left: guideTutorialCardLayout.left,
                    width: guideTutorialCardLayout.width,
                    maxHeight: guideTutorialCardLayout.maxHeight,
                    minHeight: 0,
                    overflow: "hidden",
                    boxSizing: "border-box",
                    background: "#fff",
                    borderRadius: 16,
                    padding: "14px 20px 16px",
                    border: "2px solid #3399FF",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                    zIndex: PC_GUIDE_SPOTLIGHT_Z_CARD,
                    display: "flex",
                    flexDirection: "column",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  <h3 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#111827", flexShrink: 0 }}>
                    Jouw resultaten en overzicht
                  </h3>
                  <h4 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#111827", flexShrink: 0 }}>{guideChartCard.title}</h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      color: "#4B5563",
                      lineHeight: 1.55,
                      overflowY: "auto",
                      flex: "1 1 auto",
                      minHeight: 0,
                    }}
                  >
                    {guideChartCard.body}
                  </p>
                  <div style={{ display: "flex", gap: 10, marginTop: 10, flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={goGuideInputPrevious}
                      style={{
                        padding: "12px 16px",
                        borderRadius: 10,
                        border: "2px solid #d1d5db",
                        background: "#fff",
                        color: "#374151",
                        fontWeight: 600,
                        fontSize: 15,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      Terug
                    </button>
                    <button
                      type="button"
                      onClick={goGuideInputNext}
                      style={{
                        padding: "12px 20px",
                        borderRadius: 10,
                        border: "none",
                        ...PORTAL_GRADIENT,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 18,
                        flex: 1,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {guideStep === "chartBetaalschema" ? "Rondleiding voltooien" : "Volgende"}
                    </button>
                  </div>
                </div>
              ) : null}
            </>,
            document.body
          )
        : null}

      {/* Floating Feedback Box — hidden for now; set SHOW_FEEDBACK_MODULE to true to show */}
      {SHOW_FEEDBACK_MODULE && <FeedbackForm />}
    </div>
  );
}

function FeedbackForm() {
  const [feedback, setFeedback] = useState('');
  const [studierichting, setStudierichting] = useState('');
  const [andersText, setAndersText] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [isMinimized, setIsMinimized] = useState(false);

  const studierichtingen = [
    'Economie',
    'Finance',
    'Sociologie',
    'Psychologie',
    'Rechten',
    'Geneeskunde',
    'Techniek/Engineering',
    'Informatica/IT',
    'Bedrijfskunde',
    'Marketing',
    'Communicatie',
    'Onderwijs',
    'Kunst & Cultuur',
    'Geschiedenis',
    'Filosofie',
    'Biologie',
    'Scheikunde',
    'Wiskunde',
    'Anders'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    setStatus('sending');
    try {
      const finalStudierichting = studierichting === 'Anders' ? andersText : studierichting;
      logUserActivity('feedback', {
        page: 'PortalPage',
        feedback,
        studierichting: finalStudierichting,
      });
      setStatus('success');
      setFeedback('');
      setStudierichting('');
      setAndersText('');
    } catch (err) {
      console.error('Failed to log feedback', err);
      setStatus('error');
    }
  };

  // If minimized, show only the floating icon
  if (isMinimized) {
    return (
      <div
        onClick={() => setIsMinimized(false)}
        style={{
          position: 'fixed',
          right: '28px',
          bottom: '24px',
          width: '56px',
          height: '56px',
          background: '#3399FF',
          borderRadius: '50%',
          boxShadow: '0 8px 24px rgba(51,153,255,0.4)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(51,153,255,0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(51,153,255,0.4)';
        }}
      >
        <span style={{ fontSize: 24 }}>💬</span>
      </div>
    );
  }

  // Full feedback box
  return (
    <div style={{
      position: 'fixed',
      right: '28px',
      bottom: '24px',
      width: '420px',
      maxWidth: '40vw',
      background: '#FFFFFF',
      border: '2px solid #E5E7EB',
      borderRadius: '16px',
      boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
      zIndex: 1100,
      padding: '14px 14px 12px 14px',
      overflow: 'hidden',
      fontFamily: "'Plus Jakarta Sans', sans-serif"
    }}>
      <div style={{ position: 'relative' }}>
        {/* Title with minimize button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 6,
          marginTop: -4,
          filter: status === 'success' ? 'blur(3px)' : 'none',
          transition: 'filter 180ms ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>💬</span>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1F2937' }}>We horen graag jouw mening</div>
          </div>
          {status !== 'success' && (
            <button
              onClick={() => setIsMinimized(true)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                color: '#6B7280',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#F3F4F6';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }}
              title="Minimaliseren"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
        {/* Form content (blurred when success) */}
        <form onSubmit={handleSubmit} style={{
          filter: status === 'success' ? 'blur(3px)' : 'none',
          pointerEvents: status === 'success' ? 'none' : 'auto',
          transition: 'filter 180ms ease'
        }}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ 
              display: 'block', 
              fontSize: 12, 
              fontWeight: 600, 
              color: '#374151', 
              marginBottom: 6 
            }}>
              Studierichting
            </label>
            <select
              value={studierichting}
              onChange={(e) => setStudierichting(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #E5E7EB',
                borderRadius: 10,
                fontSize: 14,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                backgroundColor: '#FFFFFF',
                color: '#1F2937',
                outline: 'none',
                cursor: 'pointer',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Selecteer studierichting</option>
              {studierichtingen.map((richting) => (
                <option key={richting} value={richting}>
                  {richting}
                </option>
              ))}
            </select>
          </div>
          {studierichting === 'Anders' && (
            <div style={{ marginBottom: 10 }}>
              <input
                type="text"
                value={andersText}
                onChange={(e) => setAndersText(e.target.value)}
                placeholder="Vul je studierichting in"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 10,
                  fontSize: 14,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          )}
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Wat vond je van deze module? Wat kon er beter? Wat vond je juist sterk?"
            style={{
              width: '100%',
              minHeight: 90,
              resize: 'vertical',
              border: '1px solid #E5E7EB',
              borderRadius: 10,
              padding: '10px 12px',
              boxSizing: 'border-box',
              fontSize: 14,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              outline: 'none'
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
            <span style={{ fontSize: 12, color: '#6B7280' }}>
              Jouw input helpt ons verbeteren
            </span>
            <button
              type="submit"
              disabled={status === 'sending'}
              style={{
                background: '#3399FF',
                color: '#FFFFFF',
                border: 'none',
                padding: '8px 12px',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 13,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                cursor: status === 'sending' ? 'not-allowed' : 'pointer',
                boxShadow: '0 8px 24px rgba(51,153,255,0.25)'
              }}
            >
              {status === 'sending' ? 'Verzenden…' : 'Verstuur'}
            </button>
          </div>
          {status === 'error' && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#DC2626' }}>
              Er ging iets mis. Probeer opnieuw.
            </div>
          )}
        </form>

        {/* Success overlay */}
        {status === 'success' && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'saturate(120%) blur(2px)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: '#EEF2FF',
                color: '#4F46E5',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                marginBottom: 10,
                boxShadow: '0 10px 24px rgba(79,70,229,0.18)'
              }}>
                🫶
              </div>
              <div style={{ fontWeight: 800, fontSize: 16, color: '#1F2937', marginBottom: 6 }}>
                Dankjewel voor jouw input!
              </div>
              <button
                onClick={() => setStatus('idle')}
                style={{
                  background: '#3399FF',
                  color: '#FFFFFF',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 13,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(51,153,255,0.25)'
                }}
              >
                Geef meer feedback
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}